import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  PlusCircle,
  Filter,
  RefreshCw,
  Calendar,
  Zap,
  ArrowDownCircle,
  ArrowUpCircle,
  ArrowLeftRight,
  Search,
  X,
  Check,
  List,
  TrendingUp,
  TrendingDown,
  Wallet,
  Edit,
  Eye,
  Trash2,
  CheckCircle,
  ChevronLeft,
  Copy,
  ChevronRight,
  FilePlus,
  FileText,
  Save,
  AlertTriangle,
  Paperclip,
  Upload,
  File,
  Download,
  Printer,
  Tag,
  Package,
} from 'lucide-react';
import { formatCurrency } from '@/utils/formatters';
import { ExportDropdown, ExportFormat } from '@/components/common/ExportDropdown';
import {
  exportToExcel,
  exportToPdf,
  ExportColumn,
  SummaryRow,
  formatPeriod,
} from '@/utils/exports';
import styles from './MovimentiPage.module.css';
import { Movimento, ContattoRubrica, Risorsa, Categoria, Articolo } from '@/types';
import movimentiService, { MovimentoFormData, GirocontoRequest, VoceRicorrente } from '@/services/movimentiService';
import rubricaService from '@/services/rubricaService';
import risorseService from '@/services/risorseService';
import categorieService from '@/services/categorieService';
import articoliService from '@/services/articoliService';
import categorieArticoliService, { CategoriaArticolo } from '@/services/categorieArticoliService';
import allegatiService, { Allegato } from '@/services/allegatiService';
import comuniService, { Comune } from '@/services/comuniService';
import tipiPagamentoService, { TipoPagamento } from '@/services/tipiPagamentoService';
import datiAziendaService, { DatiAzienda } from '@/services/datiAziendaService';
import { CarenzaMagazzino, calcolaCarenzeMagazzino, descriviCarenza, disponibilitaArticolo } from './magazzino';
import {
  scaricaOrdinePdf,
  stampaOrdinePdf,
  OrdineStampa,
  RigaOrdineStampa,
  DestinatarioStampa,
} from '@/utils/exports/ordinePdf';
import { SearchableSelect, SelectOption } from '@/components/common/SearchableSelect';
import { useTipiContatto } from '@/hooks/useTipiContatto';
import { formatTipoRisorsa } from '@/constants/tipiRisorsa';

/**
 * Filtro iniziale passato via querystring, es. /movimenti?isPagato=false
 * usato dal report per aprire direttamente lo scadenzario aperto.
 */
const readInitialPagatoFilter = () => {
  const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const isPagato = params.get('isPagato');
  return {
    soloNonPagati: isPagato === 'false',
    soloPagati: isPagato === 'true',
  };
};

export const MovimentiPage: React.FC = () => {
  const [initialPagatoFilter] = useState(readInitialPagatoFilter);

  // Tipi contatto configurabili dall'utente (pagina "Tipi Contatto")
  const { tipi: tipiContatto, label: tipoContattoLabel } = useTipiContatto();

  // State for data
  const [movimenti, setMovimenti] = useState<Movimento[]>([]);
  const [contatti, setContatti] = useState<ContattoRubrica[]>([]);
  const [risorse, setRisorse] = useState<Risorsa[]>([]);
  const [categorie, setCategorie] = useState<Categoria[]>([]);
  const [articoli, setArticoli] = useState<Articolo[]>([]);
  const [categorieArticoli, setCategorieArticoli] = useState<CategoriaArticolo[]>([]);
  const [tipiPagamento, setTipiPagamento] = useState<TipoPagamento[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  // Stampa dell'ordine da allegare alla spedizione
  const [datiAzienda, setDatiAzienda] = useState<DatiAzienda | null>(null);
  const [ordineDaStampare, setOrdineDaStampare] = useState<OrdineStampa | null>(null);
  const [stampaLoading, setStampaLoading] = useState(false);

  // State for UI
  const [showMovementFormModal, setShowModal] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Movimento | null>(null);
  const [selectedChip, setSelectedChip] = useState(
    initialPagatoFilter.soloNonPagati || initialPagatoFilter.soloPagati ? '' : 'Mese'
  );
  const [tipoFilter, setTipoFilter] = useState('tutti');
  const [pagatoChecked, setPagatoChecked] = useState(!initialPagatoFilter.soloNonPagati);
  const [nonPagatoChecked, setNonPagatoChecked] = useState(!initialPagatoFilter.soloPagati);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [categoriaFilter, setCategoriaFilter] = useState('');
  const [merceologiaFilter, setMerceologiaFilter] = useState('');
  const [articoloFilter, setArticoloFilter] = useState('');
  const [clienteFilter, setClienteFilter] = useState('');
  const [risorsaFilter, setRisorsaFilter] = useState('');
  const [tipoPagamentoFilter, setTipoPagamentoFilter] = useState('');
  const [provinciaFilter, setProvinciaFilter] = useState('');
  const [cittaFilter, setCittaFilter] = useState('');

  // Helper to format date as YYYY-MM-DD using local timezone (not UTC)
  const formatDate = (d: Date): string => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // State for date filters - use current month
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  // Con il filtro pagato/non pagato in querystring il mese corrente nasconderebbe
  // scaduto e scadenze future: si parte da una finestra ampia attorno a oggi.
  const filtroDaUrl = initialPagatoFilter.soloNonPagati || initialPagatoFilter.soloPagati;
  const [dateFrom, setDateFrom] = useState(
    formatDate(filtroDaUrl ? new Date(now.getFullYear() - 5, 0, 1) : firstDay)
  );
  const [dateTo, setDateTo] = useState(
    formatDate(filtroDaUrl ? new Date(now.getFullYear() + 5, 11, 31) : lastDay)
  );

  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // State for form modal
  const [tipoMovimento, setTipoMovimento] = useState<'entrata' | 'uscita'>('entrata');
  const [formDescrizione, setFormDescrizione] = useState('');
  const [formCategoriaId, setFormCategoriaId] = useState<number | undefined>();
  const [formClienteId, setFormClienteId] = useState<number | undefined>();
  const [formRisorsaId, setFormRisorsaId] = useState<number | undefined>();
  const [formImporto, setFormImporto] = useState('');
  const [formData, setFormData] = useState(formatDate(new Date()));
  const [formPagato, setFormPagato] = useState(true);
  const [formTipoPagamento, setFormTipoPagamento] = useState('');
  const [formArticoloId, setFormArticoloId] = useState<number | undefined>();
  const [formQuantita, setFormQuantita] = useState('1');
  const [formNote, setFormNote] = useState('');
  const [formNumeroOrdine, setFormNumeroOrdine] = useState('');
  const [ordineFilter, setOrdineFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [formErrors, setFormErrors] = useState<{categoriaId?: boolean; importo?: boolean; data?: boolean; risorsaId?: boolean}>({});

  // State for rate (installments)
  const [rateEnabled, setRateEnabled] = useState(false);
  const [formNumeroRate, setFormNumeroRate] = useState('2');
  const [formFrequenzaRate, setFormFrequenzaRate] = useState('MENSILE');
  const [formDividiImporto, setFormDividiImporto] = useState(true);
  const [editingMovimento, setEditingMovimento] = useState<Movimento | null>(null);

  // State for giroconto (transfer between accounts)
  const [isGiroconto, setIsGiroconto] = useState(false);
  // Il giroconto non riceve il numero proposto: di solito richiama un ordine esistente
  const [formOrdineGiroconto, setFormOrdineGiroconto] = useState('');
  const [formRisorsaDestinazioneId, setFormRisorsaDestinazioneId] = useState<number | undefined>();

  // State for comune/provincia with autocomplete
  const [formComune, setFormComune] = useState('');
  const [formProvincia, setFormProvincia] = useState('');
  const [comuniSuggestions, setComuniSuggestions] = useState<Comune[]>([]);
  const [showComuniDropdown, setShowComuniDropdown] = useState(false);

  // State for allegati
  const [allegati, setAllegati] = useState<Allegato[]>([]);
  const [uploadingAllegato, setUploadingAllegato] = useState(false);

  // State for province list (from comuni database)
  const [provinceList, setProvinceList] = useState<string[]>([]);
  const [cittaList, setCittaList] = useState<string[]>([]);

  // State for quick article creation modal
  const [showArticoloModal, setShowArticoloModal] = useState(false);
  const [nuovoArticoloNome, setNuovoArticoloNome] = useState('');
  const [nuovoArticoloCodice, setNuovoArticoloCodice] = useState('');
  const [nuovoArticoloPrezzoAcquisto, setNuovoArticoloPrezzoAcquisto] = useState('');
  const [nuovoArticoloPrezzoVendita, setNuovoArticoloPrezzoVendita] = useState('');
  const [nuovoArticoloQuantita, setNuovoArticoloQuantita] = useState('');
  const [nuovoArticoloCategoriaArticoloId, setNuovoArticoloCategoriaArticoloId] = useState<number | undefined>(undefined);
  const [savingArticolo, setSavingArticolo] = useState(false);

  // State for quick contact creation modal
  const [showContattoModal, setShowContattoModal] = useState(false);
  const [nuovoContattoNome, setNuovoContattoNome] = useState('');
  const [nuovoContattoEmail, setNuovoContattoEmail] = useState('');
  const [nuovoContattoTelefono, setNuovoContattoTelefono] = useState('');
  const [nuovoContattoTipo, setNuovoContattoTipo] = useState('PRIVATO');
  // Se i tipi configurati non includono PRIVATO, allineiamo la scelta di default
  useEffect(() => {
    if (tipiContatto.length > 0 && !tipiContatto.some(t => t.value === nuovoContattoTipo)) {
      setNuovoContattoTipo(tipiContatto[0].value);
    }
  }, [tipiContatto, nuovoContattoTipo]);
  const [nuovoContattoCodice, setNuovoContattoCodice] = useState('');
  const [savingContatto, setSavingContatto] = useState(false);

  // State for multi-line items (righe movimento)
  interface RigaMovimento {
    id: number;
    movimentoId?: number; // Backend movement ID (undefined for new rows added during edit)
    /** 'voce': riga con descrizione propria (spedizione, sconto merce, ...); puo' collegare un articolo da scaricare */
    tipoRiga: 'articolo' | 'voce';
    articoloId?: number;
    articoloNome?: string;
    /** Descrizione della voce libera */
    voce?: string;
    /** Reparto della voce libera; se assente vale il reparto dell'ordine */
    categoriaId?: number;
    quantita: string;
    prezzoUnitario: string;
    /** Quantità già registrata a magazzino da questa riga (solo righe esistenti, in modifica) */
    quantitaOriginale?: number;
    /** Totale digitato dall'utente: conservato per evitare lo scarto di arrotondamento (50 / 3 = 16,67 x 3 = 50,01) */
    totaleManuale?: string;
  }
  const [righeMovimento, setRigheMovimento] = useState<RigaMovimento[]>([]);
  // Voce libera in cui si sta scrivendo: la casella suggerisce anche gli articoli.
  // testoIniziale e' la voce prima della ricerca, che torna al suo posto se si sceglie un articolo.
  const [ricercaVoce, setRicercaVoce] = useState<{ rigaId: number; testoIniziale: string } | null>(null);
  const [nextRigaId, setNextRigaId] = useState(1);
  const [articoloModalRigaId, setArticoloModalRigaId] = useState<number | null>(null);
  // Valore grezzo del totale riga mentre l'utente lo digita (permette di selezionarlo e riscriverlo)
  const [editingTotale, setEditingTotale] = useState<{ rigaId: number; value: string } | null>(null);
  // Track original movement IDs loaded when editing a multi-row order
  const [originalOrderMovimentoIds, setOriginalOrderMovimentoIds] = useState<number[]>([]);
  // Voci libere gia usate negli ordini: si aggiungono con un click
  const [vociRicorrenti, setVociRicorrenti] = useState<VoceRicorrente[]>([]);

  // Voci frequenti (spedizione, imballo, ...) del tipo di movimento che si sta inserendo
  useEffect(() => {
    if (!showMovementFormModal) return;
    let attivo = true;
    movimentiService
      .getVociRicorrenti(tipoMovimento === 'uscita' ? 'USCITA' : 'ENTRATA')
      .then((voci) => { if (attivo) setVociRicorrenti(voci); })
      .catch((err) => console.error('Error loading voci ricorrenti:', err));
    return () => { attivo = false; };
  }, [showMovementFormModal, tipoMovimento]);

  // Load movimenti from backend
  const loadMovimenti = async () => {
    try {
      setLoading(true);
      setError(null);
      const filters = {
        data_inizio: dateFrom,
        data_fine: dateTo
      };
      const data = await movimentiService.getMovimenti(filters);
      setMovimenti(data);
    } catch (err) {
      console.error('Error loading movimenti:', err);
      setError('Errore nel caricamento dei movimenti');
    } finally {
      setLoading(false);
    }
  };

  // Load contatti from backend
  const loadContatti = async () => {
    try {
      const data = await rubricaService.getContatti();
      setContatti(data);
    } catch (err) {
      console.error('Error loading contatti:', err);
    }
  };

  // Load risorse from backend
  const loadRisorse = async () => {
    try {
      const data = await risorseService.getRisorse();
      setRisorse(data);
    } catch (err) {
      console.error('Error loading risorse:', err);
    }
  };

  // Load categorie from backend
  const loadCategorie = async () => {
    try {
      const data = await categorieService.getCategorie();
      setCategorie(data);
    } catch (err) {
      console.error('Error loading categorie:', err);
    }
  };

  // Load articoli from backend
  const loadArticoli = async () => {
    try {
      const data = await articoliService.getArticoli();
      setArticoli(data);
    } catch (err) {
      console.error('Error loading articoli:', err);
    }
  };

  // Dati azienda: intestazione dei documenti d'ordine (opzionali)
  const loadDatiAzienda = async () => {
    try {
      setDatiAzienda(await datiAziendaService.getDatiAzienda());
    } catch (err) {
      console.error('Error loading dati azienda:', err);
    }
  };

  // Load categorie articoli from backend
  const loadCategorieArticoli = async () => {
    try {
      const data = await categorieArticoliService.getCategorieArticoliActive();
      setCategorieArticoli(data);
    } catch (err) {
      console.error('Error loading categorie articoli:', err);
    }
  };

  // Load tipi pagamento from backend
  const loadTipiPagamento = async () => {
    try {
      const data = await tipiPagamentoService.getTipiPagamento(true); // only active
      setTipiPagamento(data);
    } catch (err) {
      console.error('Error loading tipi pagamento:', err);
    }
  };

  // Load province list from comuni database
  const loadProvince = async () => {
    try {
      const data = await comuniService.getProvince();
      setProvinceList(data);
    } catch (err) {
      console.error('Error loading province:', err);
    }
  };

  // Load allegati for a specific movimento
  const loadAllegati = async (movimentoId: number) => {
    try {
      const data = await allegatiService.getAllegatiByMovimento(movimentoId);
      setAllegati(data);
    } catch (err) {
      console.error('Error loading allegati:', err);
    }
  };

  // Upload allegato
  const handleUploadAllegato = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !editingMovimento) return;

    try {
      setUploadingAllegato(true);
      await allegatiService.uploadAllegato(file, editingMovimento.id);
      await loadAllegati(editingMovimento.id);
    } catch (err) {
      console.error('Error uploading allegato:', err);
      alert('Errore durante il caricamento del file');
    } finally {
      setUploadingAllegato(false);
      event.target.value = '';
    }
  };

  // Download allegato
  const handleDownloadAllegato = async (allegato: Allegato) => {
    try {
      await allegatiService.downloadAllegato(allegato.id, allegato.nomeFile);
    } catch (err) {
      console.error('Error downloading allegato:', err);
      alert('Errore durante il download del file');
    }
  };

  // Delete allegato
  const handleDeleteAllegato = async (allegatoId: number) => {
    if (!confirm('Sei sicuro di voler eliminare questo allegato?')) return;

    try {
      await allegatiService.deleteAllegato(allegatoId);
      if (editingMovimento) {
        await loadAllegati(editingMovimento.id);
      }
    } catch (err) {
      console.error('Error deleting allegato:', err);
      alert('Errore durante l\'eliminazione del file');
    }
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Load movimenti on component mount and when filters change
  useEffect(() => {
    loadMovimenti();
    loadContatti();
    loadRisorse();
    loadCategorie();
    loadArticoli();
    loadCategorieArticoli();
    loadDatiAzienda();
    loadTipiPagamento();
    loadProvince();
  }, [dateFrom, dateTo]);

  // Set default tipo pagamento when tipiPagamento loads
  useEffect(() => {
    if (tipiPagamento.length > 0 && !formTipoPagamento) {
      const contanti = tipiPagamento.find(t => t.codice === 'CONTANTI');
      setFormTipoPagamento(contanti ? contanti.codice : tipiPagamento[0].codice);
    }
  }, [tipiPagamento]);

  // Load cities when province filter changes
  useEffect(() => {
    const loadCitta = async () => {
      if (provinciaFilter) {
        try {
          const comuni = await comuniService.getComuniByProvincia(provinciaFilter);
          setCittaList(comuni.map(c => c.comune).sort());
        } catch (err) {
          console.error('Error loading citta:', err);
          setCittaList([]);
        }
      } else {
        setCittaList([]);
      }
    };
    loadCitta();
  }, [provinciaFilter]);

  // Convert articoli to SelectOption format for SearchableSelect
  const articoliOptions: SelectOption[] = useMemo(() => {
    return articoli.map(art => {
      const prezzo = art.prezzoVendita || art.prezzoAcquisto || 0;
      const giacenza = art.quantita ?? 0;
      // R12: la giacenza è parte dell'identità dell'articolo in fase di scelta:
      // un articolo esaurito non può essere venduto e va riconosciuto subito.
      const stock = giacenza > 0 ? `Giac. ${giacenza}` : 'ESAURITO';
      const prezzoLabel = art.codice ? `[${art.codice}] - €${prezzo}` : `€${prezzo}`;
      return {
        value: art.id,
        label: art.nome,
        subLabel: `${prezzoLabel} · ${stock}`,
      };
    });
  }, [articoli]);

  // Mappa articolo -> categoria merceologica (per il filtro per merceologia)
  const articoloCategoriaArticoloMap = useMemo(() => {
    const map = new Map<number, number>();
    for (const art of articoli) {
      if (art.categoriaArticoloId) {
        map.set(art.id, art.categoriaArticoloId);
      }
    }
    return map;
  }, [articoli]);

  // Categorie merceologiche in formato SelectOption (gerarchico: Padre > Figlio)
  const merceologieFilterOptions: SelectOption[] = useMemo(() => {
    const parents = categorieArticoli.filter(c => !c.parentId);
    const children = categorieArticoli.filter(c => c.parentId);
    const options: SelectOption[] = [];

    for (const parent of parents) {
      options.push({
        value: parent.id,
        label: parent.nome,
        subLabel: parent.descrizione || undefined,
      });

      for (const child of children.filter(c => c.parentId === parent.id)) {
        options.push({
          value: child.id,
          label: `${parent.nome} > ${child.nome}`,
          subLabel: child.descrizione || undefined,
        });
      }
    }

    // Figli orfani (padre non presente tra le categorie attive)
    const addedIds = new Set(options.map(o => o.value));
    for (const child of children) {
      if (!addedIds.has(child.id)) {
        options.push({
          value: child.id,
          label: child.nome,
          subLabel: child.descrizione || undefined,
        });
      }
    }

    return options;
  }, [categorieArticoli]);

  // Merceologia selezionata + tutte le sue sottocategorie
  const merceologiaIdsSelezionate = useMemo(() => {
    if (!merceologiaFilter) return null;
    const rootId = parseInt(merceologiaFilter);
    const ids = new Set<number>([rootId]);
    // Discesa ricorsiva sull'albero (le categorie possono avere più livelli)
    let changed = true;
    while (changed) {
      changed = false;
      for (const cat of categorieArticoli) {
        if (cat.parentId && ids.has(cat.parentId) && !ids.has(cat.id)) {
          ids.add(cat.id);
          changed = true;
        }
      }
    }
    return ids;
  }, [merceologiaFilter, categorieArticoli]);

  // Articoli selezionabili nel filtro: ristretti alla merceologia scelta
  const articoliFilterOptions: SelectOption[] = useMemo(() => {
    if (!merceologiaIdsSelezionate) return articoliOptions;
    const articoliAmmessi = new Set(
      articoli
        .filter(art => art.categoriaArticoloId && merceologiaIdsSelezionate.has(art.categoriaArticoloId))
        .map(art => art.id)
    );
    return articoliOptions.filter(o => articoliAmmessi.has(Number(o.value)));
  }, [articoliOptions, articoli, merceologiaIdsSelezionate]);

  // Convert contatti to SelectOption format for SearchableSelect
  // Mostra codice cliente in evidenza per distinguere clienti con nomi simili
  const contattiOptions: SelectOption[] = useMemo(() => {
    return contatti.map(c => {
      const codice = c.codiceProgressivo || '';
      return {
        value: c.id,
        // Per aziende: Nome - Per gli altri tipi: [CODICE] Nome
        label: c.tipoContatto === 'AZIENDA'
          ? c.nome
          : (codice ? `[${codice}] ${c.nome}` : c.nome),
        // SubLabel mostra tipo e codice per facilitare identificazione
        subLabel: `${tipoContattoLabel(c.tipoContatto)}${codice ? ` - Cod. ${codice}` : ''}`,
        // Campo searchable extra per cercare anche per codice
        searchTerms: [c.nome, codice, c.telefono || '', c.email || ''].filter(Boolean).join(' '),
      };
    });
  }, [contatti, tipoContattoLabel]);

  // Helper function to build hierarchical category options
  const buildCategorieOptions = (filtered: Categoria[], allCategories: Categoria[]): SelectOption[] => {
    const parents = filtered.filter(c => !c.parentId);
    const children = filtered.filter(c => c.parentId);
    const options: SelectOption[] = [];

    for (const parent of parents) {
      options.push({
        value: parent.id,
        label: parent.nome,
        subLabel: parent.descrizione || undefined,
      });

      const parentChildren = children.filter(c => c.parentId === parent.id);
      for (const child of parentChildren) {
        options.push({
          value: child.id,
          label: `${parent.nome} > ${child.nome}`,
          subLabel: child.descrizione || undefined,
        });
      }
    }

    // Add orphan children (parent might have been filtered out)
    const addedChildIds = new Set(options.filter(o => String(o.label).includes(' > ')).map(o => o.value));
    for (const child of children) {
      if (!addedChildIds.has(child.id)) {
        const parent = allCategories.find(c => c.id === child.parentId);
        options.push({
          value: child.id,
          label: parent ? `${parent.nome} > ${child.nome}` : child.nome,
          subLabel: child.descrizione || undefined,
        });
      }
    }

    return options;
  };

  // Categorie options filtered by tipoMovimento (for form)
  const categorieOptions: SelectOption[] = useMemo(() => {
    const filtered = categorie.filter(c => {
      // Check category's own tipo
      const ownTipoMatch = !c.tipo ||
        c.tipo.toUpperCase() === tipoMovimento.toUpperCase() ||
        c.tipo.toUpperCase() === 'ENTRAMBI';

      if (ownTipoMatch) return true;

      // For subcategories, also check parent's tipo
      if (c.parentId) {
        const parent = categorie.find(p => p.id === c.parentId);
        if (parent) {
          const parentTipoMatch = !parent.tipo ||
            parent.tipo.toUpperCase() === tipoMovimento.toUpperCase() ||
            parent.tipo.toUpperCase() === 'ENTRAMBI';
          return parentTipoMatch;
        }
      }

      return false;
    });
    return buildCategorieOptions(filtered, categorie);
  }, [categorie, tipoMovimento]);

  // Categorie options for filter (all categories)
  const categorieFilterOptions: SelectOption[] = useMemo(() => {
    return buildCategorieOptions(categorie, categorie);
  }, [categorie]);

  // Province uniche dai movimenti (per filtro) - usato come fallback se provinceList è vuoto
  const provinceUniche = useMemo(() => {
    const province = [...new Set(movimenti.map(m => m.provincia).filter(Boolean))].sort();
    return province as string[];
  }, [movimenti]);

  // Città uniche dai movimenti (per filtro), filtrate per provincia se selezionata - usato come fallback
  const cittaUniche = useMemo(() => {
    let filtered = movimenti;
    if (provinciaFilter) {
      filtered = movimenti.filter(m => m.provincia === provinciaFilter);
    }
    const citta = [...new Set(filtered.map(m => m.comune).filter(Boolean))].sort();
    return citta as string[];
  }, [movimenti, provinciaFilter]);

  // Handle tipo pagamento change - auto set isPagato
  const handleTipoPagamentoChange = (tipo: string) => {
    setFormTipoPagamento(tipo);
    // Auto-imposta Pagato basato sul tipo pagamento
    if (tipo === 'CONTANTI' || tipo === 'CARTA' || tipo === 'PAYPAL') {
      setFormPagato(true);
    } else if (tipo === 'FATTURA' || tipo === 'RIBA') {
      setFormPagato(false);
    }
  };

  // Recalculate price and validate categoria when tipo movimento changes
  useEffect(() => {
    // Recalculate price if article is selected
    if (formArticoloId && formQuantita) {
      const articolo = articoli.find(a => a.id === formArticoloId);
      if (articolo) {
        // ENTRATA = vendita (soldi in entrata) -> prezzoVendita
        // USCITA = acquisto (soldi in uscita) -> prezzoAcquisto
        const prezzo = tipoMovimento === 'entrata'
          ? (articolo.prezzoVendita || articolo.prezzoAcquisto)
          : (articolo.prezzoAcquisto || articolo.prezzoVendita);
        if (prezzo) {
          setFormImporto(String(prezzo * (parseFloat(formQuantita) || 1)));
        }
      }
    }
    // Reset categoria if it's no longer valid for the new tipo
    if (formCategoriaId) {
      const isValidCategoria = categorieOptions.some(opt => opt.value === formCategoriaId);
      if (!isValidCategoria) {
        setFormCategoriaId(undefined);
      }
    }
  }, [tipoMovimento]); // Only trigger on tipo change

  // R12: merce vendibile per l'articolo, tenendo conto di quanto la riga ha già scaricato
  const getDisponibilitaArticolo = useCallback(
    (articoloId: number): number =>
      disponibilitaArticolo(articoloId, {
        articoli,
        righe: righeMovimento,
        movimentoInModifica: editingMovimento,
      }),
    [articoli, righeMovimento, editingMovimento]
  );

  // R12: articoli dell'ordine che il magazzino non riesce a coprire (solo vendite)
  const carenzeMagazzino = useMemo<CarenzaMagazzino[]>(
    () =>
      calcolaCarenzeMagazzino({
        tipoMovimento,
        isGiroconto,
        articoli,
        righe: righeMovimento,
        articoloSingoloId: formArticoloId,
        quantitaSingola: formQuantita,
        movimentoInModifica: editingMovimento,
      }),
    [tipoMovimento, isGiroconto, articoli, righeMovimento, formArticoloId, formQuantita, editingMovimento]
  );

  // Handle articolo change - auto set prezzo e categoria
  const handleArticoloChange = (articoloId: number | undefined) => {
    setFormArticoloId(articoloId);
    if (articoloId) {
      const articolo = articoli.find(a => a.id === articoloId);
      if (articolo) {
        // Se importo vuoto, suggerisci prezzo * quantita
        // ENTRATA = vendita (soldi in entrata) -> prezzoVendita
        // USCITA = acquisto (soldi in uscita) -> prezzoAcquisto
        const prezzo = tipoMovimento === 'entrata'
          ? (articolo.prezzoVendita || articolo.prezzoAcquisto)
          : (articolo.prezzoAcquisto || articolo.prezzoVendita);
        if (!formImporto && prezzo) {
          const qta = parseFloat(formQuantita) || 1;
          setFormImporto(String(prezzo * qta));
        }
        // Se articolo ha categoria, impostala
        if (articolo.categoriaId) {
          setFormCategoriaId(articolo.categoriaId);
        }
      }
    }
  };

  // Handle quantita change - ricalcola importo
  const handleQuantitaChange = (qta: string) => {
    setFormQuantita(qta);
    if (formArticoloId) {
      const articolo = articoli.find(a => a.id === formArticoloId);
      // ENTRATA = vendita (soldi in entrata) -> prezzoVendita
      // USCITA = acquisto (soldi in uscita) -> prezzoAcquisto
      const prezzo = tipoMovimento === 'entrata'
        ? (articolo?.prezzoVendita || articolo?.prezzoAcquisto)
        : (articolo?.prezzoAcquisto || articolo?.prezzoVendita);
      if (prezzo) {
        setFormImporto(String(prezzo * (parseFloat(qta) || 1)));
      }
    }
  };

  // Handle comune input change - search for suggestions
  const handleComuneInputChange = async (value: string) => {
    setFormComune(value);
    if (value.length >= 2) {
      try {
        const suggestions = await comuniService.searchComuni(value);
        setComuniSuggestions(suggestions);
        setShowComuniDropdown(suggestions.length > 0);
      } catch (err) {
        console.error('Error fetching comuni suggestions:', err);
        setComuniSuggestions([]);
      }
    } else {
      setComuniSuggestions([]);
      setShowComuniDropdown(false);
    }
  };

  // Handle comune selection from dropdown - auto-populate provincia
  const handleComuneSelect = (comune: Comune) => {
    setFormComune(comune.comune);
    setFormProvincia(comune.provincia);
    setComuniSuggestions([]);
    setShowComuniDropdown(false);
  };

  // Handle quick article creation
  const handleSaveNuovoArticolo = async () => {
    if (!nuovoArticoloNome.trim()) {
      alert('Il nome articolo è obbligatorio');
      return;
    }
    if (!nuovoArticoloCategoriaArticoloId) {
      alert('La categoria merceologica è obbligatoria');
      return;
    }

    setSavingArticolo(true);
    try {
      const nuovoArticolo = await articoliService.createArticolo({
        nome: nuovoArticoloNome.trim(),
        codice: nuovoArticoloCodice.trim() || undefined,
        prezzoAcquisto: nuovoArticoloPrezzoAcquisto ? parseFloat(nuovoArticoloPrezzoAcquisto) : undefined,
        prezzoVendita: nuovoArticoloPrezzoVendita ? parseFloat(nuovoArticoloPrezzoVendita) : undefined,
        quantita: nuovoArticoloQuantita ? parseFloat(nuovoArticoloQuantita) : 0,
        categoriaArticoloId: nuovoArticoloCategoriaArticoloId,
        attivo: true,
      });

      // Reload articoli list
      await loadArticoli();

      // Select the new article in the correct context
      if (articoloModalRigaId !== null) {
        // Multi-row mode: update the specific row
        const prezzo = tipoMovimento === 'entrata'
          ? (nuovoArticolo.prezzoVendita || nuovoArticolo.prezzoAcquisto)
          : (nuovoArticolo.prezzoAcquisto || nuovoArticolo.prezzoVendita);
        setRigheMovimento(prev => prev.map(r => {
          if (r.id !== articoloModalRigaId) return r;
          return {
            ...r,
            articoloId: nuovoArticolo.id,
            articoloNome: nuovoArticolo.nome,
            prezzoUnitario: prezzo ? String(prezzo) : r.prezzoUnitario,
          };
        }));
        setArticoloModalRigaId(null);
      } else {
        // Single mode: set form field
        setFormArticoloId(nuovoArticolo.id);

        // Set importo based on tipo and prezzo
        const prezzo = tipoMovimento === 'entrata'
          ? (nuovoArticolo.prezzoVendita || nuovoArticolo.prezzoAcquisto)
          : (nuovoArticolo.prezzoAcquisto || nuovoArticolo.prezzoVendita);
        if (prezzo) {
          const qta = parseFloat(formQuantita) || 1;
          setFormImporto(String(prezzo * qta));
        }
      }

      // Close modal and reset fields
      setShowArticoloModal(false);
      setNuovoArticoloNome('');
      setNuovoArticoloCodice('');
      setNuovoArticoloPrezzoAcquisto('');
      setNuovoArticoloPrezzoVendita('');
      setNuovoArticoloQuantita('');
      setNuovoArticoloCategoriaArticoloId(undefined);
    } catch (err) {
      console.error('Error creating articolo:', err);
      alert('Errore nella creazione dell\'articolo');
    } finally {
      setSavingArticolo(false);
    }
  };

  // Handle quick contact creation
  const handleSaveNuovoContatto = async () => {
    if (!nuovoContattoNome.trim()) {
      alert('Il nome è obbligatorio');
      return;
    }

    setSavingContatto(true);
    try {
      const nuovoContatto = await rubricaService.createContatto({
        nome: nuovoContattoNome.trim(),
        email: nuovoContattoEmail.trim() || undefined,
        telefono: nuovoContattoTelefono.trim() || undefined,
        tipoContatto: nuovoContattoTipo,
        codiceProgressivo: nuovoContattoCodice.trim() || undefined,
        generaCodiceAutomatico: !nuovoContattoCodice.trim(),
      });

      // Reload contatti list
      await loadContatti();

      // Select the new contact
      setFormClienteId(nuovoContatto.id);

      // Close modal and reset fields
      setShowContattoModal(false);
      setNuovoContattoNome('');
      setNuovoContattoEmail('');
      setNuovoContattoTelefono('');
      setNuovoContattoTipo('PRIVATO');
      setNuovoContattoCodice('');
    } catch (err) {
      console.error('Error creating contatto:', err);
      alert('Errore nella creazione del contatto');
    } finally {
      setSavingContatto(false);
    }
  };

  // Handle adding a new riga to multi-line movimento
  const handleAddRiga = () => {
    const newRiga: RigaMovimento = {
      id: nextRigaId,
      tipoRiga: 'articolo',
      quantita: '1',
      prezzoUnitario: '',
    };
    setRigheMovimento([...righeMovimento, newRiga]);
    setNextRigaId(nextRigaId + 1);
  };

  // Voce libera: riga senza articolo (spedizione, imballo, ...), eventualmente da una voce gia usata
  const handleAddVoce = (ricorrente?: VoceRicorrente) => {
    const newRiga: RigaMovimento = {
      id: nextRigaId,
      tipoRiga: 'voce',
      voce: ricorrente?.voce || '',
      categoriaId: ricorrente?.categoriaId,
      quantita: '1',
      prezzoUnitario: ricorrente ? ricorrente.prezzoUnitario.toFixed(2) : '',
    };
    setRigheMovimento([...righeMovimento, newRiga]);
    setNextRigaId(nextRigaId + 1);
  };

  // Articoli suggeriti scrivendo nella descrizione di una voce libera
  const articoliPerVoce = (testo: string) => {
    const cerca = testo.trim().toLowerCase();
    if (cerca.length < 2) return [];
    return articoli
      .filter(a => a.nome.toLowerCase().includes(cerca) || a.codice?.toLowerCase().includes(cerca))
      .slice(0, 8);
  };

  // La voce resta (es. "Sconto merce") e scarica l'articolo scelto; il prezzo e' quello della voce
  const collegaArticoloAVoce = (rigaId: number, articolo?: Articolo, testoVoce?: string) => {
    setRigheMovimento(righe => righe.map(r => r.id !== rigaId ? r : {
      ...r,
      voce: testoVoce ?? r.voce,
      articoloId: articolo?.id,
      articoloNome: articolo?.nome,
    }));
  };

  // Campi del movimento che dipendono dal tipo di riga (articolo o voce libera)
  const datiRiga = (riga: RigaMovimento) => riga.tipoRiga === 'voce'
    ? {
        descrizione: riga.voce?.trim() || riga.articoloNome || formDescrizione || undefined,
        voce: riga.voce?.trim() || undefined,
        articoloId: riga.articoloId,
        categoriaId: riga.categoriaId || formCategoriaId!,
      }
    : {
        descrizione: riga.articoloNome || formDescrizione || undefined,
        articoloId: riga.articoloId,
        categoriaId: formCategoriaId!,
      };

  // I due movimenti di un giroconto possono portare il numero di un ordine, ma non ne sono righe
  const eGiroconto = (mov: Movimento) => mov.categoria?.nome === 'Giroconto';

  // Riga di un ordine gia salvato: con una voce (o senza articolo) torna voce libera col proprio reparto
  const rigaDaMovimento = (mov: Movimento, id: number, articoloNome?: string): RigaMovimento => {
    const quantita = mov.quantita || 1;
    const base = {
      id,
      quantita: String(quantita),
      prezzoUnitario: String((mov.importo / quantita).toFixed(2)),
    };
    return mov.articolo_id && !mov.voce
      ? { ...base, tipoRiga: 'articolo', articoloId: mov.articolo_id, articoloNome: articoloNome || mov.descrizione || '' }
      : {
          ...base,
          tipoRiga: 'voce',
          voce: mov.voce || '',
          categoriaId: mov.categoria_id,
          articoloId: mov.articolo_id,
          articoloNome: mov.articolo_id ? (articoloNome || mov.articolo?.nome || '') : undefined,
        };
  };

  // Numero ordine proposto all'apertura: evita di salvare, riaprire e copiarlo.
  // Se l'utente ha gia scritto un numero, il suo resta.
  const proponiNumeroOrdine = async () => {
    try {
      const numero = await movimentiService.getProssimoNumeroOrdine();
      setFormNumeroOrdine(corrente => corrente || numero);
    } catch (err) {
      // Senza proposta il numero viene generato al salvataggio, come prima
      console.error('Error loading prossimo numero ordine:', err);
    }
  };

  // Handle removing a riga
  const handleRemoveRiga = (rigaId: number) => {
    setRigheMovimento(righeMovimento.filter(r => r.id !== rigaId));
  };

  // Handle updating a riga
  const handleUpdateRiga = (rigaId: number, field: keyof RigaMovimento, value: string | number | undefined) => {
    setRigheMovimento(righeMovimento.map(r => {
      if (r.id !== rigaId) return r;
      const updated = { ...r, [field]: value };
      // If articolo changed, update prezzo
      if (field === 'articoloId' && value) {
        const articolo = articoli.find(a => a.id === value);
        if (articolo) {
          updated.articoloNome = articolo.nome;
          const prezzo = tipoMovimento === 'entrata'
            ? (articolo.prezzoVendita || articolo.prezzoAcquisto)
            : (articolo.prezzoAcquisto || articolo.prezzoVendita);
          if (prezzo) {
            updated.prezzoUnitario = String(prezzo);
          }
        }
      }
      // Cambiando quantità, prezzo o articolo il totale torna calcolato
      if (field === 'quantita' || field === 'prezzoUnitario' || field === 'articoloId') {
        updated.totaleManuale = undefined;
      }
      return updated;
    }));
  };

  // Handle updating totale riga (calcola prezzo unitario)
  const handleUpdateRigaTotale = (rigaId: number, totaleRiga: string) => {
    setRigheMovimento(righeMovimento.map(r => {
      if (r.id !== rigaId) return r;
      const quantita = parseFloat(r.quantita) || 1;
      const totale = parseFloat(totaleRiga) || 0;
      const prezzoUnitario = quantita > 0 ? (totale / quantita) : 0;
      return {
        ...r,
        prezzoUnitario: prezzoUnitario.toFixed(2),
        totaleManuale: totaleRiga,
      };
    }));
  };

  // Calculate totale for a single riga (il totale digitato dall'utente prevale)
  const calcolaTotaleRiga = (riga: RigaMovimento): number => {
    if (riga.totaleManuale !== undefined && riga.totaleManuale !== '') {
      return parseFloat(riga.totaleManuale) || 0;
    }
    const qta = parseFloat(riga.quantita) || 0;
    const prezzo = parseFloat(riga.prezzoUnitario) || 0;
    return qta * prezzo;
  };

  // Importo salvato per la riga: lo stesso totale mostrato nel form
  const importoRiga = (riga: RigaMovimento): number => {
    if (riga.totaleManuale !== undefined && riga.totaleManuale !== '') {
      return parseFloat(riga.totaleManuale) || 0;
    }
    return (parseFloat(riga.quantita) || 1) * (parseFloat(riga.prezzoUnitario) || 0);
  };

  // Calculate total from righe
  const calcolaTotaleRighe = () => {
    return righeMovimento.reduce((sum, riga) => sum + calcolaTotaleRiga(riga), 0);
  };

  // Helper: Check if a movimento should use multi-row mode
  // All movements from 2026-02-15 onwards MUST use multi-row mode
  const shouldUseMultiRow = (dataMovimento: string): boolean => {
    const cutoffDate = '2026-02-15';
    return dataMovimento >= cutoffDate;
  };

  // Handle duplica movimento - fetch full details first
  const handleDuplicaMovimento = async (movimento: Movimento) => {
    try {
      // Fetch full movement details (includes articoloId, risorsaId, clienteId)
      const fullMovimento = await movimentiService.getMovimentoById(movimento.id);

      setTipoMovimento((fullMovimento.tipo?.toLowerCase() || 'entrata') as 'entrata' | 'uscita');
      setFormDescrizione(fullMovimento.descrizione || '');
      setFormCategoriaId(fullMovimento.categoria_id);
      setFormClienteId(fullMovimento.rubrica_id);
      setFormRisorsaId(fullMovimento.risorsa_id);
      setFormTipoPagamento(fullMovimento.tipoPagamento || getDefaultTipoPagamento());
      setFormNote(fullMovimento.note || '');
      // Nuovo numero ordine per il duplicato (non si copia l'originale)
      setFormNumeroOrdine('');
      proponiNumeroOrdine();
      setFormComune(fullMovimento.comune || '');
      setFormProvincia(fullMovimento.provincia || '');
      // Data = oggi, non pagato
      const today = formatDate(new Date());
      setFormData(today);
      setFormPagato(false);
      setEditingMovimento(null);
      setIsViewMode(false); // La duplica apre il form in modifica, mai in sola lettura
      setAllegati([]);
      setOriginalOrderMovimentoIds([]); // Duplication creates new movements

      // From 2026-02-15 onwards, ALL movements must use multi-row mode
      const useMultiRow = shouldUseMultiRow(today);

      if (useMultiRow) {
        // Multi-row mode: load all articles into righeMovimento table
        const righe: RigaMovimento[] = [];
        let nextId = 1;

        if (fullMovimento.numeroOrdine) {
          // Load all movements with same numeroOrdine
          const movimentiConStessoOrdine = await movimentiService.getMovimenti({
            ordine: fullMovimento.numeroOrdine
          });

          for (const mov of movimentiConStessoOrdine) {
            if (mov.articolo_id || mov.voce) {
              const articolo = articoli.find(a => a.id === mov.articolo_id);
              righe.push(rigaDaMovimento(mov, nextId++, articolo?.nome));
            }
          }
        } else if (fullMovimento.articolo_id) {
          // Single article: add it as one row in the table
          const articolo = articoli.find(a => a.id === fullMovimento.articolo_id);
          const quantita = fullMovimento.quantita || 1;
          const prezzoUnitario = fullMovimento.importo / quantita;

          righe.push({
            id: nextId++,
            tipoRiga: 'articolo',
            articoloId: fullMovimento.articolo_id,
            articoloNome: articolo?.nome || fullMovimento.descrizione || '',
            quantita: String(quantita),
            prezzoUnitario: String(prezzoUnitario.toFixed(2)),
          });
        }

        setRigheMovimento(righe);
        setNextRigaId(nextId);
        setFormArticoloId(undefined);
        setFormQuantita('1');
        // If there are article rows, importo is calculated; otherwise keep original
        setFormImporto(righe.length > 0 ? '0' : String(fullMovimento.importo));
      } else {
        // Legacy mode (before 2026-02-15): use old logic
        if (fullMovimento.numeroOrdine) {
          const movimentiConStessoOrdine = await movimentiService.getMovimenti({
            ordine: fullMovimento.numeroOrdine
          });

          if (movimentiConStessoOrdine.length > 1) {
            const righe: RigaMovimento[] = [];
            let nextId = 1;

            for (const mov of movimentiConStessoOrdine) {
              if (mov.articolo_id) {
                const articolo = articoli.find(a => a.id === mov.articolo_id);
                const quantita = mov.quantita || 1;
                const prezzoUnitario = mov.importo / quantita;

                righe.push({
                  id: nextId++,
                  tipoRiga: 'articolo',
                  articoloId: mov.articolo_id,
                  articoloNome: articolo?.nome || mov.descrizione || '',
                  quantita: String(quantita),
                  prezzoUnitario: String(prezzoUnitario.toFixed(2)),
                });
              }
            }

            setRigheMovimento(righe);
            setNextRigaId(nextId);
            setFormArticoloId(undefined);
            setFormQuantita('1');
            setFormImporto('0');
          } else {
            setFormArticoloId(fullMovimento.articolo_id);
            setFormQuantita(String(fullMovimento.quantita || 1));
            setFormImporto(String(fullMovimento.importo));
            setRigheMovimento([]);
            setNextRigaId(1);
          }
        } else {
          setFormArticoloId(fullMovimento.articolo_id);
          setFormQuantita(String(fullMovimento.quantita || 1));
          setFormImporto(String(fullMovimento.importo));
          setRigheMovimento([]);
          setNextRigaId(1);
        }
      }

      setShowModal(true);
    } catch (err) {
      console.error('Error loading movimento details:', err);
      alert('Errore nel caricamento del movimento');
    }
  };

  // Get default tipo pagamento - prefer CONTANTI, fallback to first available
  const getDefaultTipoPagamento = () => {
    const contanti = tipiPagamento.find(t => t.codice === 'CONTANTI');
    if (contanti) return contanti.codice;
    return tipiPagamento.length > 0 ? tipiPagamento[0].codice : '';
  };

  // Reset form
  const resetForm = () => {
    setFormDescrizione('');
    setFormImporto('');
    setFormCategoriaId(undefined);
    setFormClienteId(undefined);
    setFormRisorsaId(undefined);
    setFormData(formatDate(new Date()));
    setFormPagato(true);
    setFormTipoPagamento(getDefaultTipoPagamento());
    setFormArticoloId(undefined);
    setFormQuantita('1');
    setFormNote('');
    setFormNumeroOrdine('');
    setFormComune('');
    setFormProvincia('');
    setComuniSuggestions([]);
    setShowComuniDropdown(false);
    setTipoMovimento('entrata');
    setFormErrors({});
    setEditingMovimento(null);
    setIsViewMode(false);
    // Reset rate fields
    setRateEnabled(false);
    setFormNumeroRate('2');
    setFormFrequenzaRate('MENSILE');
    setFormDividiImporto(true);
    // Reset multi-line fields
    setRigheMovimento([]);
    setNextRigaId(1);
    setOriginalOrderMovimentoIds([]);
    // Reset giroconto fields
    setIsGiroconto(false);
    setFormOrdineGiroconto('');
    setFormRisorsaDestinazioneId(undefined);
  };

  // Handle save movimento
  const handleSaveMovimento = async () => {
    // Validate required fields
    const errors: {categoriaId?: boolean; importo?: boolean; data?: boolean; risorsaId?: boolean; risorsaDestinazioneId?: boolean} = {};

    // For giroconto, categoria is auto-assigned
    if (!isGiroconto && !formCategoriaId) errors.categoriaId = true;


    // For multi-line, validate total; for single, validate importo
    // Accept zero for "sconto merce" cases - but field must not be empty
    const importoStr = righeMovimento.length > 0 ? String(calcolaTotaleRighe()) : formImporto;
    const effectiveImporto = parseFloat(importoStr);
    // Allow zero (sconto merce), reject only empty/null, NaN, or negative
    // Note: parseFloat("0") = 0 which is valid, parseFloat("") = NaN which is invalid
    const isImportoEmpty = importoStr === '' || importoStr == null;
    const isImportoInvalid = isImportoEmpty || isNaN(effectiveImporto) || effectiveImporto < 0;
    if (isImportoInvalid) errors.importo = true;
    if (!formData) errors.data = true;
    if (!formRisorsaId) errors.risorsaId = true;

    // Giroconto-specific validations
    if (isGiroconto) {
      if (!formRisorsaDestinazioneId) {
        errors.risorsaDestinazioneId = true;
      } else if (formRisorsaDestinazioneId === formRisorsaId) {
        alert('La risorsa di destinazione deve essere diversa da quella di origine');
        return;
      }
    }

    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    // R12: le righe vengono salvate una alla volta; senza questo controllo il backend
    // rifiuta la riga scoperta a metà ordine lasciando salvate le precedenti.
    if (carenzeMagazzino.length > 0) {
      const dettaglio = carenzeMagazzino.map(c => '• ' + descriviCarenza(c)).join('\n');
      alert('Magazzino insufficiente: la vendita non può essere registrata.\n\n' + dettaglio + '\n\nRegistra prima il rifornimento (movimento di uscita/acquisto) oppure riduci le quantità.');
      return;
    }

    // Una voce libera nuova deve dire che cosa e' (spedizione, imballo, ...): finirebbe senza nome in stampa
    const vociSenzaNome = righeMovimento.filter(r =>
      r.tipoRiga === 'voce' && !r.movimentoId && !r.voce?.trim() && !r.articoloId && calcolaTotaleRiga(r) !== 0);
    if (!isGiroconto && vociSenzaNome.length > 0) {
      alert('Inserisci la descrizione delle voci libere (es. "Spese di spedizione").');
      return;
    }

    try {
      // Giroconto: transfer between accounts
      if (isGiroconto && !editingMovimento) {
        const girocontoData: GirocontoRequest = {
          importo: parseFloat(formImporto),
          data: formData,
          risorsaId: formRisorsaId!,
          risorsaDestinazioneId: formRisorsaDestinazioneId!,
          note: formNote || undefined,
          numeroOrdine: formOrdineGiroconto.trim() || undefined,
        };
        console.log('Creating giroconto with data:', girocontoData);
        console.log('formRisorsaId:', formRisorsaId, 'formRisorsaDestinazioneId:', formRisorsaDestinazioneId);
        console.log('Available risorse:', risorse.map(r => ({ id: r.id, nome: r.nome })));
        await movimentiService.createGiroconto(girocontoData);
      }
      // Multi-line editing: update each row individually (no delete-and-recreate)
      else if (righeMovimento.length > 0 && editingMovimento && formNumeroOrdine) {
        // Track which original movements are still present
        const currentMovimentoIds = new Set(
          righeMovimento.filter(r => r.movimentoId).map(r => r.movimentoId!)
        );

        // Delete movements that were removed by the user
        for (const origId of originalOrderMovimentoIds) {
          if (!currentMovimentoIds.has(origId)) {
            await movimentiService.deleteMovimento(origId);
          }
        }

        // Update existing rows and create new ones
        for (const riga of righeMovimento) {
          const rigaImporto = importoRiga(riga);
          if (!riga.articoloId && !riga.voce?.trim() && rigaImporto === 0) continue; // Skip truly empty rows

          const movimentoData: MovimentoFormData = {
            tipo: tipoMovimento.toUpperCase() as 'ENTRATA' | 'USCITA',
            ...datiRiga(riga),
            importo: rigaImporto,
            dataMovimento: formData,
            isPagato: formPagato,
            clienteId: formClienteId,
            risorsaId: formRisorsaId,
            tipoPagamento: formTipoPagamento,
            quantita: parseFloat(riga.quantita) || 1,
            note: formNote || undefined,
            numeroOrdine: formNumeroOrdine,
            comune: formComune || undefined,
            provincia: formProvincia || undefined,
          };

          if (riga.movimentoId) {
            // Existing row: update only this movement
            await movimentiService.updateMovimento(riga.movimentoId, movimentoData);
          } else {
            // New row added during edit: create
            await movimentiService.createMovimento(movimentoData);
          }
        }
      }
      // Multi-line: create one movement per riga
      else if (righeMovimento.length > 0 && !editingMovimento) {
        // Generate a common group identifier (using timestamp + random)
        const gruppoOrdine = formNumeroOrdine || `ORD-${Date.now()}`;

        for (const riga of righeMovimento) {
          const rigaImporto = importoRiga(riga);
          if (!riga.articoloId && !riga.voce?.trim() && rigaImporto === 0) continue; // Skip truly empty rows

          const movimentoData: MovimentoFormData = {
            tipo: tipoMovimento.toUpperCase() as 'ENTRATA' | 'USCITA',
            ...datiRiga(riga),
            importo: rigaImporto,
            dataMovimento: formData,
            isPagato: formPagato,
            clienteId: formClienteId,
            risorsaId: formRisorsaId,
            tipoPagamento: formTipoPagamento,
            quantita: parseFloat(riga.quantita) || 1,
            note: formNote || undefined,
            numeroOrdine: gruppoOrdine,
            comune: formComune || undefined,
            provincia: formProvincia || undefined,
          };

          await movimentiService.createMovimento(movimentoData);
        }
      } else {
        // Single movimento (original logic)
        const movimentoData: MovimentoFormData = {
          tipo: tipoMovimento.toUpperCase() as 'ENTRATA' | 'USCITA',
          descrizione: formDescrizione || undefined,
          importo: parseFloat(formImporto),
          dataMovimento: formData,
          isPagato: formPagato,
          categoriaId: formCategoriaId!,
          clienteId: formClienteId,
          risorsaId: formRisorsaId,
          tipoPagamento: formTipoPagamento,
          articoloId: formArticoloId,
          quantita: formQuantita ? parseFloat(formQuantita) : undefined,
          note: formNote || undefined,
          numeroOrdine: formNumeroOrdine || `ORD-${Date.now()}`,
          comune: formComune || undefined,
          provincia: formProvincia || undefined,
          // Rate fields (only when creating, not editing)
          ...(rateEnabled && !editingMovimento ? {
            numeroRate: parseInt(formNumeroRate),
            frequenzaRate: formFrequenzaRate,
            dataScadenza: formData,
            dividiImporto: formDividiImporto,
          } : {}),
        };

        if (editingMovimento) {
          await movimentiService.updateMovimento(editingMovimento.id, movimentoData);
        } else {
          await movimentiService.createMovimento(movimentoData);
        }
      }

      // Reset form
      setShowModal(false);
      resetForm();

      // Reload data
      await loadMovimenti();
      // Reload risorse to update saldi (especially important for giroconti)
      await loadRisorse();
      // R12: il movimento ha modificato le giacenze: ricaricale per gli avvisi successivi
      await loadArticoli();
    } catch (err) {
      console.error('Error saving movimento:', err);
      // Il backend dice perché ha rifiutato (es. giacenza insufficiente): mostrarlo,
      // altrimenti l'utente vede solo un movimento che non si chiude.
      const messaggioBackend = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      alert(messaggioBackend
        ? 'Errore nel salvataggio del movimento:\n\n' + messaggioBackend
        : 'Errore nel salvataggio del movimento');
      // Le righe già salvate hanno modificato il magazzino: riallinea giacenze e lista
      await loadMovimenti();
      await loadArticoli();
    }
  };

  // Handle edit movimento - fetch full details first
  const handleViewMovimento = async (movimento: Movimento) => {
    await handleEditMovimento(movimento);
    setIsViewMode(true);
  };

  const handleEditMovimento = async (movimento: Movimento) => {
    try {
      setIsViewMode(false);
      // Fetch full movement details (includes articoloId, risorsaId, clienteId)
      const fullMovimento = await movimentiService.getMovimentoById(movimento.id);

      setEditingMovimento(fullMovimento);
      setFormDescrizione(fullMovimento.descrizione || '');
      setTipoMovimento(fullMovimento.tipo);
      setFormCategoriaId(fullMovimento.categoria_id);
      setFormRisorsaId(fullMovimento.risorsa_id);
      setFormClienteId(fullMovimento.rubrica_id);
      setFormData(fullMovimento.data);
      setFormPagato(fullMovimento.isPagato ?? true);
      setFormTipoPagamento(fullMovimento.tipoPagamento || getDefaultTipoPagamento());
      setFormNote(fullMovimento.note || '');
      setFormNumeroOrdine(fullMovimento.numeroOrdine || '');
      setFormComune(fullMovimento.comune || '');
      setFormProvincia(fullMovimento.provincia || '');
      loadAllegati(fullMovimento.id);

      // Load all rows of the order, fetching full details per row to preserve quantita
      const righe: RigaMovimento[] = [];
      let nextId = 1;
      const loadedMovimentoIds: number[] = [];

      if (fullMovimento.numeroOrdine && !eGiroconto(fullMovimento)) {
        // Load all movements with same numeroOrdine
        const movimentiConStessoOrdine = await movimentiService.getMovimenti({
          ordine: fullMovimento.numeroOrdine
        });

        for (const mov of movimentiConStessoOrdine.filter(m => !eGiroconto(m))) {
          // Fetch full details to get quantita (summary API may not include it)
          const fullMov = await movimentiService.getMovimentoById(mov.id);
          loadedMovimentoIds.push(fullMov.id);

          const articolo = fullMov.articolo_id ? articoli.find(a => a.id === fullMov.articolo_id) : undefined;
          righe.push({
            ...rigaDaMovimento(fullMov, nextId++, articolo?.nome),
            movimentoId: fullMov.id,
            quantitaOriginale: fullMov.quantita || 1,
          });
        }
      } else {
        // Single movement (no order group)
        loadedMovimentoIds.push(fullMovimento.id);
        const articolo = fullMovimento.articolo_id ? articoli.find(a => a.id === fullMovimento.articolo_id) : undefined;
        righe.push({
          ...rigaDaMovimento(fullMovimento, nextId++, articolo?.nome),
          movimentoId: fullMovimento.id,
          quantitaOriginale: fullMovimento.quantita || 1,
        });
      }

      setOriginalOrderMovimentoIds(loadedMovimentoIds);

      if (righe.length > 0) {
        setRigheMovimento(righe);
        setNextRigaId(nextId);
        setFormArticoloId(undefined);
        setFormQuantita('1');
        setFormImporto('0'); // Calculated from rows
      } else {
        setRigheMovimento([]);
        setNextRigaId(1);
        setFormArticoloId(fullMovimento.articolo_id);
        setFormQuantita(String(fullMovimento.quantita || 1));
        setFormImporto(String(fullMovimento.importo));
      }

      setShowModal(true);
    } catch (err) {
      console.error('Error loading movimento details:', err);
      alert('Errore nel caricamento del movimento');
    }
  };

  /**
   * Reparto del movimento nella forma "Reparto > Sottoreparto" usata nel form.
   * La categoria va cercata nell'anagrafica: quella allegata al movimento è
   * sintetizzata dal service e non porta il riferimento al reparto padre.
   */
  const nomeReparto = (movimento: Movimento): string | undefined => {
    const categoria = categorie.find(c => c.id === movimento.categoria_id) || movimento.categoria;
    if (!categoria) return undefined;
    const parent = categoria.parentId ? categorie.find(c => c.id === categoria.parentId) : undefined;
    return parent ? `${parent.nome} > ${categoria.nome}` : categoria.nome;
  };

  // Handle delete movimento
  /**
   * Raccoglie tutte le righe dell'ordine (i movimenti con lo stesso numero
   * ordine) e apre la scelta del formato di stampa.
   */
  const handleStampaOrdine = async (movimento: Movimento) => {
    if (!movimento.numeroOrdine) return;

    try {
      setStampaLoading(true);

      const movimentiOrdine = (await movimentiService.getMovimenti({ ordine: movimento.numeroOrdine }))
        .filter(m => !eGiroconto(m));
      const righeOrdine = (movimentiOrdine.length > 0 ? movimentiOrdine : [movimento]);

      const righe: RigaOrdineStampa[] = righeOrdine.map(m => {
        const articolo = articoli.find(a => a.id === m.articolo_id);
        const quantita = m.quantita || 1;
        return {
          codice: articolo?.codice,
          descrizione: m.voce && articolo
            ? `${m.voce} – ${articolo.nome}`
            : articolo?.nome || m.descrizione || (m.articolo_id ? 'Articolo' : 'Voce'),
          reparto: nomeReparto(m),
          quantita,
          prezzoUnitario: quantita > 0 ? m.importo / quantita : m.importo,
          voceLibera: !m.articolo_id,
        };
      });

      // Il destinatario è il contatto dell'ordine (di norma uguale su tutte le righe)
      const conContatto = righeOrdine.find(m => m.rubrica_id) || movimento;
      const contatto = contatti.find(c => c.id === conContatto.rubrica_id);
      const destinatario: DestinatarioStampa | undefined = contatto
        ? {
            nome: contatto.azienda || [contatto.nome, contatto.cognome].filter(Boolean).join(' '),
            indirizzo: contatto.indirizzo,
            cap: contatto.cap,
            citta: contatto.citta,
            provincia: contatto.provincia,
            partitaIva: contatto.partitaIva,
            codiceFiscale: contatto.codiceFiscale,
            telefono: contatto.telefono,
            email: contatto.email,
          }
        : undefined;

      const note = righeOrdine.map(m => m.note).filter(Boolean).join(' · ');

      setOrdineDaStampare({
        numeroOrdine: movimento.numeroOrdine,
        data: movimento.data,
        destinatario,
        righe,
        note: note || undefined,
        conPrezzi: true,
        azienda: datiAzienda,
      });
    } catch (err) {
      console.error('Error loading ordine for print:', err);
      alert('Errore nel caricamento dell\'ordine da stampare');
    } finally {
      setStampaLoading(false);
    }
  };

  /** Genera il documento nel formato scelto: anteprima di stampa o download. */
  const eseguiStampaOrdine = (conPrezzi: boolean, azione: 'stampa' | 'download') => {
    if (!ordineDaStampare) return;
    const documento = { ...ordineDaStampare, conPrezzi, azienda: datiAzienda };
    if (azione === 'stampa') {
      stampaOrdinePdf(documento);
    } else {
      scaricaOrdinePdf(documento);
    }
    setOrdineDaStampare(null);
  };

  const handleDeleteClick = (movimento: Movimento) => {
    setDeleteTarget(movimento);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      await movimentiService.deleteMovimento(deleteTarget.id);
      await loadMovimenti();
      setShowDeleteModal(false);
      setDeleteTarget(null);
    } catch (err) {
      console.error('Error deleting movimento:', err);
      alert('Errore nell\'eliminazione del movimento');
    }
  };

  // Apply frontend filters to movimenti
  const filteredMovimenti = movimenti.filter(m => {
    // Tipo filter
    if (tipoFilter === 'entrate' && m.tipo !== 'entrata') return false;
    if (tipoFilter === 'uscite' && m.tipo !== 'uscita') return false;

    // Pagato filter
    const isPagato = m.isPagato ?? true;
    if (!pagatoChecked && isPagato) return false;
    if (!nonPagatoChecked && !isPagato) return false;

    // Categoria filter
    if (categoriaFilter && m.categoria_id !== parseInt(categoriaFilter)) return false;

    // Merceologia filter (categoria merceologica dell'articolo del movimento)
    if (merceologiaIdsSelezionate) {
      if (!m.articolo_id) return false;
      const categoriaArticoloId = articoloCategoriaArticoloMap.get(m.articolo_id);
      if (!categoriaArticoloId || !merceologiaIdsSelezionate.has(categoriaArticoloId)) return false;
    }

    // Articolo filter
    if (articoloFilter && m.articolo_id !== parseInt(articoloFilter)) return false;

    // Cliente filter
    if (clienteFilter && m.rubrica_id !== parseInt(clienteFilter)) return false;

    // Risorsa filter
    if (risorsaFilter && m.risorsa_id !== parseInt(risorsaFilter)) return false;

    // Numero ordine filter (ricerca parziale)
    if (ordineFilter && (!m.numeroOrdine || !m.numeroOrdine.toLowerCase().includes(ordineFilter.toLowerCase()))) return false;

    // Tipo pagamento filter
    if (tipoPagamentoFilter && m.tipoPagamento !== tipoPagamentoFilter) return false;

    // Provincia filter
    if (provinciaFilter && m.provincia !== provinciaFilter) return false;

    // Città filter
    if (cittaFilter && m.comune !== cittaFilter) return false;

    // Search text filter (cerca in descrizione, note, cliente, categoria)
    if (searchText) {
      const search = searchText.toLowerCase();
      const matchDescrizione = m.descrizione?.toLowerCase().includes(search);
      const matchNote = m.note?.toLowerCase().includes(search);
      const matchCliente = m.contatto?.nome?.toLowerCase().includes(search);
      const matchCategoria = m.categoria?.nome?.toLowerCase().includes(search);
      const matchNumeroOrdine = m.numeroOrdine?.toLowerCase().includes(search);
      const matchImporto = m.importo?.toString().includes(search);
      if (!matchDescrizione && !matchNote && !matchCliente && !matchCategoria && !matchNumeroOrdine && !matchImporto) {
        return false;
      }
    }

    return true;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredMovimenti.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedMovimenti = filteredMovimenti.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [tipoFilter, pagatoChecked, nonPagatoChecked, categoriaFilter, merceologiaFilter, articoloFilter, clienteFilter, risorsaFilter, ordineFilter, tipoPagamentoFilter, provinciaFilter, cittaFilter, searchText, dateFrom, dateTo]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleItemsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const calculateTotals = () => {
    // Escludi giroconti dai totali (sono trasferimenti interni, non entrate/uscite reali)
    const isNotGiroconto = (m: Movimento) => m.categoria?.nome !== 'Giroconto';
    // Totali solo per movimenti PAGATI (esclusi giroconti)
    const entrate = filteredMovimenti
      .filter(m => m.tipo === 'entrata' && m.isPagato === true && isNotGiroconto(m))
      .reduce((sum, m) => sum + m.importo, 0);
    const uscite = filteredMovimenti
      .filter(m => m.tipo === 'uscita' && m.isPagato === true && isNotGiroconto(m))
      .reduce((sum, m) => sum + m.importo, 0);
    // Totali NON pagati (da incassare/pagare) - esclusi giroconti
    const daIncassare = filteredMovimenti
      .filter(m => m.tipo === 'entrata' && m.isPagato === false && isNotGiroconto(m))
      .reduce((sum, m) => sum + m.importo, 0);
    const daPagare = filteredMovimenti
      .filter(m => m.tipo === 'uscita' && m.isPagato === false && isNotGiroconto(m))
      .reduce((sum, m) => sum + m.importo, 0);
    return { entrate, uscite, saldo: entrate - uscite, daIncassare, daPagare };
  };

  const totals = calculateTotals();

  const handleSelectAll = () => {
    if (selectedIds.size === filteredMovimenti.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredMovimenti.map(m => m.id)));
    }
  };

  const handleToggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(id)) {
        newSelected.delete(id);
      } else {
        newSelected.add(id);
      }
      return newSelected;
    });
  };

  const handleExport = async (format: ExportFormat) => {
    if (filteredMovimenti.length === 0) {
      alert('Nessun dato da esportare');
      return;
    }

    setExportLoading(true);
    try {
      const columns: ExportColumn[] = [
        { field: 'data', header: 'Data', type: 'date', width: 11 },
        { field: 'tipo', header: 'Tipo', type: 'text', width: 9 },
        { field: 'numeroOrdine', header: 'Ordine', type: 'text', width: 14 },
        { field: 'descrizione', header: 'Descrizione', type: 'text', width: 22 },
        { field: 'reparto', header: 'Reparto', type: 'text', width: 16 },
        { field: 'contatto', header: 'Contatto', type: 'text', width: 18 },
        { field: 'articolo', header: 'Articolo', type: 'text', width: 18 },
        { field: 'quantita', header: 'Q.tà', type: 'number', width: 8 },
        { field: 'risorsaNome', header: 'Risorsa', type: 'text', width: 15 },
        { field: 'risorsaTipo', header: 'Tipo Risorsa', type: 'text', width: 14 },
        { field: 'tipoPagamentoNome', header: 'Tipo Pag.', type: 'text', width: 14 },
        { field: 'importo', header: 'Importo', type: 'currency', width: 13 },
        { field: 'isPagatoText', header: 'Pagato', type: 'text', width: 10 },
        { field: 'rata', header: 'Rata', type: 'text', width: 8 },
        { field: 'dataScadenza', header: 'Scadenza', type: 'date', width: 13 },
        { field: 'comune', header: 'Comune', type: 'text', width: 14 },
        { field: 'provincia', header: 'Prov.', type: 'text', width: 7 },
      ];

      const exportData = filteredMovimenti.map(m => ({
        data: m.data,
        tipo: m.tipo === 'entrata' ? 'Entrata' : 'Uscita',
        numeroOrdine: m.numeroOrdine || '',
        descrizione: m.descrizione || m.note || '',
        reparto: m.categoria?.nome || '',
        contatto: m.contatto
          ? `${m.contatto.codiceProgressivo ? m.contatto.codiceProgressivo + ' - ' : ''}${m.contatto.nome}`
          : '',
        articolo: m.articolo?.nome || '',
        quantita: m.quantita ?? null,
        risorsaNome: m.risorsa?.nome || '',
        risorsaTipo: formatTipoRisorsa(m.risorsa?.tipo),
        tipoPagamentoNome: tipiPagamento.find(t => t.codice === m.tipoPagamento)?.nome || m.tipoPagamento || '',
        importo: m.importo,
        isPagatoText: m.isPagato !== false ? 'Sì' : 'No',
        rata: m.totaleRate && m.totaleRate > 1 ? `${m.numeroRata || 1}/${m.totaleRate}` : '',
        dataScadenza: m.dataScadenza || '',
        comune: m.comune || '',
        provincia: m.provincia || '',
      }));

      const summaryData: SummaryRow[] = [
        { label: 'Totale Entrate', value: totals.entrate, type: 'currency' },
        { label: 'Totale Uscite', value: totals.uscite, type: 'currency' },
        { label: 'Saldo', value: totals.saldo, type: 'currency' },
      ];

      const startDate = dateFrom ? new Date(dateFrom) : null;
      const endDate = dateTo ? new Date(dateTo) : null;
      const periodString = formatPeriod(startDate, endDate);

      const config = {
        title: 'Movimenti',
        filename: 'movimenti',
        columns,
        orientation: 'landscape' as const,
        summaryData,
      };

      if (format === 'excel') {
        await exportToExcel(config, exportData);
      } else {
        await exportToPdf(config, exportData, periodString ? `Periodo: ${periodString}` : undefined);
      }
    } catch (err) {
      console.error('Error exporting movimenti:', err);
      alert('Errore durante l\'esportazione');
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportSelected = () => {
    if (selectedIds.size === 0) return;

    const selectedMovimenti = filteredMovimenti.filter(m => selectedIds.has(m.id));

    // Generate CSV - stesse colonne dell'export Excel/PDF
    const quote = (value?: string | null) => `"${(value || '').replace(/"/g, '""')}"`;

    let csv = 'Data,Tipo,Ordine,Descrizione,Reparto,Contatto,Articolo,Q.ta,Risorsa,Tipo Risorsa,' +
      'Tipo Pagamento,Importo,Pagato,Rata,Scadenza,Comune,Prov.\n';
    for (const m of selectedMovimenti) {
      const row = [
        m.data || '',
        m.tipo,
        quote(m.numeroOrdine),
        quote(m.descrizione || m.note),
        quote(m.categoria?.nome),
        quote(m.contatto
          ? `${m.contatto.codiceProgressivo ? m.contatto.codiceProgressivo + ' - ' : ''}${m.contatto.nome}`
          : ''),
        quote(m.articolo?.nome),
        m.quantita != null ? m.quantita.toString() : '',
        quote(m.risorsa?.nome),
        quote(formatTipoRisorsa(m.risorsa?.tipo)),
        quote(tipiPagamento.find(t => t.codice === m.tipoPagamento)?.nome || m.tipoPagamento),
        m.importo.toString(),
        m.isPagato !== false ? 'Si' : 'No',
        m.totaleRate && m.totaleRate > 1 ? `${m.numeroRata || 1}/${m.totaleRate}` : '',
        m.dataScadenza || '',
        quote(m.comune),
        quote(m.provincia),
      ];
      csv += row.join(',') + '\n';
    }

    // Download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `movimenti_selezionati_${formatDate(new Date())}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;

    if (!confirm(`Eliminare ${selectedIds.size} movimenti selezionati?`)) return;

    try {
      for (const id of selectedIds) {
        await movimentiService.deleteMovimento(id);
      }
      setSelectedIds(new Set());
      await loadMovimenti();
    } catch (err) {
      console.error('Error deleting movements:', err);
      alert('Errore nell\'eliminazione dei movimenti');
    }
  };

  const handleApplyFilters = () => {
    loadMovimenti();
  };

  const handleResetFilters = () => {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    setDateFrom(formatDate(first));
    setDateTo(formatDate(last));
    setTipoFilter('tutti');
    setPagatoChecked(true);
    setNonPagatoChecked(true);
    setCategoriaFilter('');
    setArticoloFilter('');
    setClienteFilter('');
    setRisorsaFilter('');
    setOrdineFilter('');
    setTipoPagamentoFilter('');
    setProvinciaFilter('');
    setCittaFilter('');
    setSearchText('');
    setSelectedChip('Mese');
  };

  const handleQuickFilter = (chip: string) => {
    setSelectedChip(chip);
    const now = new Date();

    if (chip === 'Oggi') {
      const today = formatDate(now);
      setDateFrom(today);
      setDateTo(today);
    } else if (chip === 'Mese') {
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      setDateFrom(formatDate(firstDay));
      setDateTo(formatDate(lastDay));
    } else if (chip === 'Anno') {
      const firstDay = new Date(now.getFullYear(), 0, 1);
      const lastDay = new Date(now.getFullYear(), 11, 31);
      setDateFrom(formatDate(firstDay));
      setDateTo(formatDate(lastDay));
    }
  };

  return (
    <div className={styles.container}>
      <main>
        {/* Page Header */}
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>Movimenti</h1>
          <div className={styles.pageActions}>
            <ExportDropdown
              onExport={handleExport}
              disabled={filteredMovimenti.length === 0}
              loading={exportLoading}
            />
          </div>
        </div>

        {/* Stats Bar - Compact horizontal summary */}
        <div className={styles.statsBar}>
          <div className={styles.statItem}>
            <TrendingUp size={20} style={{ color: 'var(--success-main)' }} />
            <span className={styles.statNumber}>{formatCurrency(totals.entrate)}</span>
            <span className={styles.statText}>Entrate</span>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.statItem}>
            <TrendingDown size={20} style={{ color: 'var(--error-main)' }} />
            <span className={styles.statNumber}>{formatCurrency(totals.uscite)}</span>
            <span className={styles.statText}>Uscite</span>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.statItem}>
            <Wallet size={20} style={{ color: totals.saldo >= 0 ? 'var(--success-main)' : 'var(--error-main)' }} />
            <span className={styles.statNumber} style={{ color: totals.saldo >= 0 ? 'var(--success-main)' : 'var(--error-main)' }}>
              {formatCurrency(totals.saldo)}
            </span>
            <span className={styles.statText}>Saldo</span>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.statItem}>
            <ArrowDownCircle size={20} style={{ color: 'var(--warning-main, #ff9800)' }} />
            <span className={styles.statNumber} style={{ color: 'var(--warning-main, #ff9800)' }}>
              {formatCurrency(totals.daIncassare)}
            </span>
            <span className={styles.statText}>Da Incassare</span>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.statItem}>
            <ArrowUpCircle size={20} style={{ color: 'var(--info-main, #2196f3)' }} />
            <span className={styles.statNumber} style={{ color: 'var(--info-main, #2196f3)' }}>
              {formatCurrency(totals.daPagare)}
            </span>
            <span className={styles.statText}>Da Pagare</span>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.statItem}>
            <List size={20} style={{ color: 'var(--primary-main)' }} />
            <span className={styles.statNumber}>{filteredMovimenti.length}</span>
            <span className={styles.statText}>Movimenti</span>
          </div>
        </div>

        {/* Filter Panel */}
        <div className={styles.filterPanel}>
          <div className={styles.filterHeader}>
            <h6>
              <Filter size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
              FILTRI
            </h6>
            <button className={styles.btnSecondarySmall} onClick={handleResetFilters}>
              <RefreshCw size={14} />
              Reset
            </button>
          </div>

          {/* Periodo e Quick Filters */}
          <div className={styles.mb4}>
            <label className={styles.formLabel}>
              <Calendar size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
              Periodo
            </label>
            <div className={styles.periodControls}>
              <div className={styles.dateRange}>
                <input
                  type="date"
                  className={styles.formControl}
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
                <span>-</span>
                <input
                  type="date"
                  className={styles.formControl}
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>
              <div className={styles.quickFilters}>
                <span className={`${styles.bodySmall} ${styles.quickFiltersLabel}`}>
                  <Zap size={14} />
                  Rapidi:
                </span>
                {['Oggi', 'Mese', 'Anno'].map((chip) => (
                  <button
                    key={chip}
                    className={`${styles.chip} ${selectedChip === chip ? styles.active : ''}`}
                    onClick={() => handleQuickFilter(chip)}
                  >
                    {chip}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Tipo Movimento */}
          <div className={styles.mb4}>
            <label className={styles.formLabel}>Tipo</label>
            <div className={styles.radioGroup}>
              <label className={styles.radioWrapper}>
                <input
                  type="radio"
                  name="tipo-filter"
                  value="tutti"
                  className={styles.radio}
                  checked={tipoFilter === 'tutti'}
                  onChange={(e) => setTipoFilter(e.target.value)}
                />
                <span>Tutti</span>
              </label>
              <label className={styles.radioWrapper}>
                <input
                  type="radio"
                  name="tipo-filter"
                  value="entrate"
                  className={styles.radio}
                  checked={tipoFilter === 'entrate'}
                  onChange={(e) => setTipoFilter(e.target.value)}
                />
                <ArrowDownCircle size={16} style={{ color: 'var(--success-main)' }} />
                <span>Entrate</span>
              </label>
              <label className={styles.radioWrapper}>
                <input
                  type="radio"
                  name="tipo-filter"
                  value="uscite"
                  className={styles.radio}
                  checked={tipoFilter === 'uscite'}
                  onChange={(e) => setTipoFilter(e.target.value)}
                />
                <ArrowUpCircle size={16} style={{ color: 'var(--error-main)' }} />
                <span>Uscite</span>
              </label>
            </div>
          </div>

          {/* Other Filters */}
          <div className={styles.filterRow}>
            <div className={styles.formGroup}>
              <SearchableSelect
                options={categorieFilterOptions}
                value={categoriaFilter ? parseInt(categoriaFilter) : null}
                onChange={(val) => setCategoriaFilter(val ? String(val) : '')}
                placeholder="Tutti i reparti"
                label="Reparto"
                clearable={true}
              />
            </div>
            <div className={styles.formGroup}>
              <SearchableSelect
                options={merceologieFilterOptions}
                value={merceologiaFilter ? parseInt(merceologiaFilter) : null}
                onChange={(val) => { setMerceologiaFilter(val ? String(val) : ''); setArticoloFilter(''); }}
                placeholder="Tutte le merceologie"
                label="Merceologia"
                clearable={true}
              />
            </div>
            <div className={styles.formGroup}>
              <SearchableSelect
                options={articoliFilterOptions}
                value={articoloFilter ? parseInt(articoloFilter) : null}
                onChange={(val) => setArticoloFilter(val ? String(val) : '')}
                placeholder="Cerca articolo..."
                label="Articolo"
                clearable={true}
              />
            </div>
            <div className={styles.formGroup}>
              <SearchableSelect
                options={contattiOptions}
                value={clienteFilter ? parseInt(clienteFilter) : null}
                onChange={(val) => setClienteFilter(val ? String(val) : '')}
                placeholder="Cerca contatto..."
                label="Contatto"
                clearable={true}
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Risorsa</label>
              <select
                className={styles.formControl}
                value={risorsaFilter}
                onChange={(e) => setRisorsaFilter(e.target.value)}
              >
                <option value="">Tutte</option>
                {risorse.map((risorsa) => (
                  <option key={risorsa.id} value={risorsa.id}>{risorsa.nome}</option>
                ))}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>N. Ordine</label>
              <input
                type="text"
                className={styles.formControl}
                placeholder="Cerca ordine..."
                value={ordineFilter}
                onChange={(e) => setOrdineFilter(e.target.value)}
              />
            </div>
          </div>

          {/* Filtri Tipo Pagamento, Provincia, Città */}
          <div className={styles.filterRow}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Tipo Pagamento</label>
              <select
                className={styles.formControl}
                value={tipoPagamentoFilter}
                onChange={(e) => setTipoPagamentoFilter(e.target.value)}
              >
                <option value="">Tutti</option>
                {tipiPagamento.map((tipo) => (
                  <option key={tipo.id} value={tipo.codice}>{tipo.descrizione}</option>
                ))}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Provincia</label>
              <select
                className={styles.formControl}
                value={provinciaFilter}
                onChange={(e) => { setProvinciaFilter(e.target.value); setCittaFilter(''); }}
              >
                <option value="">Tutte</option>
                {(provinceList.length > 0 ? provinceList : provinceUniche).map((prov) => (
                  <option key={prov} value={prov}>{prov}</option>
                ))}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Città</label>
              <select
                className={styles.formControl}
                value={cittaFilter}
                onChange={(e) => setCittaFilter(e.target.value)}
                disabled={!provinciaFilter}
              >
                <option value="">{provinciaFilter ? 'Tutte' : 'Seleziona provincia'}</option>
                {(cittaList.length > 0 ? cittaList : cittaUniche).map((citta) => (
                  <option key={citta} value={citta}>{citta}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Stato e Cerca */}
          <div className={styles.filterLastRow}>
            <div>
              <label className={styles.formLabel}>Stato</label>
              <div className={styles.checkboxGroup}>
                <label className={styles.checkboxWrapper}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={pagatoChecked}
                    onChange={(e) => setPagatoChecked(e.target.checked)}
                  />
                  <span>Pagati</span>
                </label>
                <label className={styles.checkboxWrapper}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={nonPagatoChecked}
                    onChange={(e) => setNonPagatoChecked(e.target.checked)}
                  />
                  <span>Non Pagati</span>
                </label>
              </div>
            </div>
            <div className={styles.searchField}>
              <label className={styles.formLabel}>Cerca</label>
              <div className={styles.searchInputWrapper}>
                <Search size={18} className={styles.searchIcon} />
                <input
                  type="text"
                  className={styles.formControlSearch}
                  placeholder="Cerca in descrizione, note, cliente..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Filter Actions */}
          <div className={styles.filterActions}>
            <button className={styles.btnSecondary} onClick={handleResetFilters}>
              <X size={16} />
              Pulisci
            </button>
            <button className={styles.btnPrimary} onClick={handleApplyFilters}>
              <Check size={16} />
              Applica Filtri
            </button>
            <button className={styles.btnPrimary} onClick={() => { resetForm(); setAllegati([]); setShowModal(true); proponiNumeroOrdine(); }}>
              <PlusCircle size={18} />
              Nuovo Movimento
            </button>
          </div>
        </div>

        {/* Table Card */}
        <div className={styles.card}>
          {loading && (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <p>Caricamento...</p>
            </div>
          )}
          {error && (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--error-main)' }}>
              <p>{error}</p>
            </div>
          )}
          {!loading && !error && (
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        className={styles.checkbox}
                        checked={selectedIds.size === filteredMovimenti.length && filteredMovimenti.length > 0}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th>Data</th>
                    <th>Tipo</th>
                    <th>Ordine</th>
                    <th>Reparto</th>
                    <th>Contatto</th>
                    <th>Articolo</th>
                    <th>Risorsa</th>
                    <th>Tipo Pag.</th>
                    <th className={styles.textRight}>Importo</th>
                    <th>Pag.</th>
                    <th className={styles.actionsHeader}>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedMovimenti.length === 0 ? (
                    <tr>
                      <td colSpan={12} style={{ textAlign: 'center', padding: '2rem' }}>
                        Nessun movimento trovato
                      </td>
                    </tr>
                  ) : (
                    paginatedMovimenti.map((movimento) => (
                      <tr key={movimento.id}>
                        <td>
                          <input
                            type="checkbox"
                            className={styles.checkbox}
                            checked={selectedIds.has(movimento.id)}
                            onChange={() => handleToggleSelect(movimento.id)}
                          />
                        </td>
                        <td>{new Date(movimento.data).toLocaleDateString('it-IT')}</td>
                        <td>
                          {movimento.tipo === 'entrata' ? (
                            <ArrowDownCircle size={18} style={{ color: 'var(--success-main)' }} />
                          ) : (
                            <ArrowUpCircle size={18} style={{ color: 'var(--error-main)' }} />
                          )}
                        </td>
                        <td>{movimento.numeroOrdine || '-'}</td>
                        <td>{movimento.categoria?.nome || '-'}</td>
                        <td>{movimento.contatto ? `${movimento.contatto.codiceProgressivo ? movimento.contatto.codiceProgressivo + ' - ' : ''}${movimento.contatto.nome}` : '-'}</td>
                        <td>
                          {movimento.articolo?.nome || (movimento.voce
                            ? <span className={styles.voceLiberaCell} title="Voce libera (senza articolo)"><Tag size={12} /> {movimento.voce}</span>
                            : '-')}
                        </td>
                        <td>
                          {movimento.risorsa ? (
                            <span>
                              {movimento.risorsa.nome}
                              {movimento.risorsa.tipo ? (
                                <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                                  {formatTipoRisorsa(movimento.risorsa.tipo)}
                                </span>
                              ) : null}
                            </span>
                          ) : '-'}
                        </td>
                        <td>{tipiPagamento.find(t => t.codice === movimento.tipoPagamento)?.nome || movimento.tipoPagamento || '-'}</td>
                        <td className={`${styles.textRight} ${styles.fw600} ${movimento.tipo === 'entrata' ? styles.textSuccess : styles.textError}`}>
                          {movimento.tipo === 'uscita' ? '-' : ''}{formatCurrency(movimento.importo)}
                        </td>
                        <td>
                          <span
                            className={`${styles.badge} ${movimento.isPagato !== false ? styles.badgeSuccess : styles.badgeDanger}`}
                          >
                            {movimento.isPagato !== false ? <CheckCircle size={12} /> : <X size={12} />}
                          </span>
                        </td>
                        <td className={styles.actionsCell}>
                          <button
                            className={styles.btnIcon}
                            title="Visualizza"
                            onClick={() => handleViewMovimento(movimento)}
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            className={styles.btnIcon}
                            title="Duplica"
                            onClick={() => handleDuplicaMovimento(movimento)}
                          >
                            <Copy size={16} />
                          </button>
                          <button
                            className={styles.btnIcon}
                            title="Modifica"
                            onClick={() => handleEditMovimento(movimento)}
                          >
                            <Edit size={16} />
                          </button>
                          {movimento.numeroOrdine && !eGiroconto(movimento) && (
                            <button
                              className={styles.btnIcon}
                              title="Stampa ordine per la spedizione"
                              onClick={() => handleStampaOrdine(movimento)}
                              disabled={stampaLoading}
                            >
                              <Printer size={16} />
                            </button>
                          )}
                          <button
                            className={styles.btnIcon}
                            title="Elimina"
                            onClick={() => handleDeleteClick(movimento)}
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Table Footer Actions */}
          {!loading && !error && (
            <>
              <div className={styles.tableFooter}>
                <div className={styles.tableFooterLeft}>
                  <label className={styles.checkboxWrapper}>
                    <input
                      type="checkbox"
                      className={styles.checkbox}
                      checked={selectedIds.size === filteredMovimenti.length && filteredMovimenti.length > 0}
                      onChange={handleSelectAll}
                    />
                    <span>Seleziona tutti ({selectedIds.size})</span>
                  </label>
                  {selectedIds.size > 0 && (
                    <>
                      <button className={styles.btnDangerSmall} onClick={handleDeleteSelected}>
                        <Trash2 size={14} />
                        Elimina selezionati ({selectedIds.size})
                      </button>
                      <button className={styles.btnSecondarySmall} onClick={handleExportSelected}>
                        <Download size={14} />
                        Esporta selezionati
                      </button>
                    </>
                  )}
                </div>
                <div className={styles.tableFooterRight}>
                  <span className={styles.bodySmall}>Mostra:</span>
                  <select
                    className={styles.formControlSmall}
                    value={itemsPerPage}
                    onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                  >
                    <option value={25}>25 per pagina</option>
                    <option value={50}>50 per pagina</option>
                    <option value={100}>100 per pagina</option>
                  </select>
                </div>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className={styles.pagination}>
                  <button
                    className={styles.pageBtn}
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    <ChevronLeft size={14} />
                  </button>
                  {getPageNumbers().map((page, index) => (
                    typeof page === 'number' ? (
                      <button
                        key={index}
                        className={`${styles.pageBtn} ${currentPage === page ? styles.active : ''}`}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    ) : (
                      <span key={index} className={styles.pageEllipsis}>...</span>
                    )
                  ))}
                  <button
                    className={styles.pageBtn}
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Modal Nuovo Movimento */}
      {showMovementFormModal && (
        <div className={styles.modalOverlay} onMouseDown={(e) => { if (e.target === e.currentTarget) { setShowModal(false); setIsViewMode(false); } }}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                {isViewMode
                  ? <Eye size={24} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                  : <FilePlus size={24} style={{ verticalAlign: 'middle', marginRight: '8px' }} />}
                {isViewMode ? 'Dettaglio Movimento' : (editingMovimento ? 'Modifica Movimento' : 'Nuovo Movimento')}
              </h3>
              <button className={styles.modalClose} onClick={() => { setShowModal(false); setIsViewMode(false); }}>
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <fieldset disabled={isViewMode} style={{ border: 'none', padding: 0, margin: 0, minInlineSize: 'auto' }}>
              {carenzeMagazzino.length > 0 && !isViewMode && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '10px 12px', marginBottom: '12px', borderRadius: '8px', background: 'var(--warning-light, #fff4e5)', color: 'var(--warning-dark, #663c00)', border: '1px solid var(--warning-main, #ff9800)' }}>
                  <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    <strong>Magazzino insufficiente:</strong> il movimento non può essere salvato.
                    <ul style={{ margin: '4px 0 4px 0', paddingLeft: '18px' }}>
                      {carenzeMagazzino.map(c => (
                        <li key={c.articoloId}>{descriviCarenza(c)}</li>
                      ))}
                    </ul>
                    Registra prima il rifornimento (movimento di uscita/acquisto) oppure riduci le quantità.
                  </div>
                </div>
              )}
              <div className={styles.mb6}>
                <label className={styles.formLabel}>Tipo Movimento *</label>
                <div className={styles.typeButtonGroup}>
                  <button
                    type="button"
                    className={`${styles.typeButton} ${styles.entrata} ${!isGiroconto && tipoMovimento === 'entrata' ? styles.selected : ''}`}
                    onClick={() => { setIsGiroconto(false); setTipoMovimento('entrata'); }}
                    disabled={editingMovimento !== null}
                  >
                    <span className={styles.typeButtonIcon}>
                      <ArrowDownCircle size={28} />
                    </span>
                    <span className={styles.typeButtonLabel}>Entrata</span>
                  </button>
                  <button
                    type="button"
                    className={`${styles.typeButton} ${styles.uscita} ${!isGiroconto && tipoMovimento === 'uscita' ? styles.selected : ''}`}
                    onClick={() => { setIsGiroconto(false); setTipoMovimento('uscita'); }}
                    disabled={editingMovimento !== null}
                  >
                    <span className={styles.typeButtonIcon}>
                      <ArrowUpCircle size={28} />
                    </span>
                    <span className={styles.typeButtonLabel}>Uscita</span>
                  </button>
                  <button
                    type="button"
                    className={`${styles.typeButton} ${styles.giroconto} ${isGiroconto ? styles.selected : ''}`}
                    onClick={() => { setIsGiroconto(true); }}
                    disabled={editingMovimento !== null}
                  >
                    <span className={styles.typeButtonIcon}>
                      <ArrowLeftRight size={28} />
                    </span>
                    <span className={styles.typeButtonLabel}>Giroconto</span>
                  </button>
                </div>
              </div>

              {/* Sezione: Chi e Cosa - nascosta per giroconti */}
              {!isGiroconto && (
              <div className={styles.section}>
                <h4 className={styles.sectionHeader}>
                  <FileText size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                  Chi e Cosa
                </h4>
                <div className={`${styles.grid} ${styles.grid2}`}>
                  <div className={styles.formGroup}>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                      <div style={{ flex: 1 }}>
                        <SearchableSelect
                          options={contattiOptions}
                          value={formClienteId || null}
                          onChange={(val) => setFormClienteId(val ? Number(val) : undefined)}
                          placeholder="Cerca contatto/fornitore..."
                          label="Contatto/Fornitore"
                          clearable={true}
                          disabled={isViewMode}
                        />
                      </div>
                      <button
                        type="button"
                        className={styles.iconButton}
                        onClick={() => setShowContattoModal(true)}
                        title="Crea nuovo contatto"
                        style={{
                          padding: '8px 12px',
                          backgroundColor: 'var(--primary-main)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '38px',
                        }}
                      >
                        <PlusCircle size={16} />
                      </button>
                    </div>
                  </div>
                  {/* Campi Articolo/Quantità singoli - nascosti se multi-riga attivo */}
                  {righeMovimento.length === 0 && editingMovimento && (
                    <>
                      <div className={styles.formGroup}>
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                          <div style={{ flex: 1 }}>
                            <SearchableSelect
                              options={articoliOptions}
                              value={formArticoloId || null}
                              onChange={(val) => handleArticoloChange(val ? Number(val) : undefined)}
                              placeholder="Cerca articolo..."
                              label="Articolo (singolo)"
                              clearable={true}
                              disabled={isViewMode}
                            />
                          </div>
                          <button
                            type="button"
                            className={styles.iconButton}
                            onClick={() => setShowArticoloModal(true)}
                            title="Crea nuovo articolo"
                            style={{
                              padding: '8px 12px',
                              backgroundColor: 'var(--primary-main)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              height: '38px',
                            }}
                          >
                            <PlusCircle size={16} />
                          </button>
                        </div>
                      </div>
                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Quantità</label>
                        <input
                          type="number"
                          className={styles.formControl}
                          placeholder="1"
                          step="1"
                          min="1"
                          value={formQuantita}
                          onChange={(e) => handleQuantitaChange(e.target.value)}
                        />
                      </div>
                    </>
                  )}
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>N. Ordine</label>
                    <input
                      type="text"
                      className={styles.formControl}
                      placeholder="Numero ordine..."
                      value={formNumeroOrdine}
                      onChange={(e) => setFormNumeroOrdine(e.target.value)}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <SearchableSelect
                      options={categorieOptions}
                      value={formCategoriaId || null}
                      onChange={(val) => { setFormCategoriaId(val ? Number(val) : undefined); setFormErrors(prev => ({...prev, categoriaId: false})); }}
                      placeholder="Cerca reparto..."
                      label="Reparto *"
                      error={formErrors.categoriaId ? 'Campo obbligatorio' : undefined}
                      clearable={true}
                      disabled={isViewMode}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Tipo Pagamento *</label>
                    <select
                      className={styles.formControl}
                      value={formTipoPagamento}
                      onChange={(e) => handleTipoPagamentoChange(e.target.value)}
                    >
                      {tipiPagamento.map((tipo) => (
                        <option key={tipo.codice} value={tipo.codice}>{tipo.nome}</option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.formGroup} style={{ position: 'relative' }}>
                    <label className={styles.formLabel}>Comune</label>
                    <input
                      type="text"
                      className={styles.formControl}
                      placeholder="Cerca comune..."
                      value={formComune}
                      onChange={(e) => handleComuneInputChange(e.target.value)}
                      onBlur={() => setTimeout(() => setShowComuniDropdown(false), 200)}
                      onFocus={() => comuniSuggestions.length > 0 && setShowComuniDropdown(true)}
                    />
                    {showComuniDropdown && comuniSuggestions.length > 0 && (
                      <div className={styles.autocompleteDropdown}>
                        {comuniSuggestions.slice(0, 10).map((comune) => (
                          <div
                            key={comune.id}
                            className={styles.autocompleteItem}
                            onClick={() => handleComuneSelect(comune)}
                          >
                            <span>{comune.comune}</span>
                            <span className={styles.autocompleteSubtext}>({comune.siglaProvincia})</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Provincia</label>
                    <input
                      type="text"
                      className={styles.formControl}
                      placeholder="Provincia..."
                      value={formProvincia}
                      onChange={(e) => setFormProvincia(e.target.value)}
                    />
                  </div>
                </div>
              </div>
              )}

              {/* Sezione: Righe Articoli (Multi-riga) - nascosta per giroconti */}
              {!isGiroconto && righeMovimento.length > 0 && (
                <div className={styles.section}>
                  <h4 className={styles.sectionHeader}>
                    <List size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                    Righe Ordine ({righeMovimento.length})
                  </h4>
                  {/* Header labels */}
                  <div className={`${styles.rigaGrid} ${styles.rigaGridHeader}`}>
                    <div>Articolo / Voce</div>
                    <div></div>
                    <div style={{ textAlign: 'center' }}>Qtà</div>
                    <div style={{ textAlign: 'right' }}>Prezzo Unit.</div>
                    <div style={{ textAlign: 'right' }}>Totale Riga</div>
                    <div></div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {righeMovimento.map((riga) => {
                      const totaleRiga = calcolaTotaleRiga(riga);
                      // R12: giacenza della riga, calcolata solo per le vendite
                      const carenzaRiga = riga.articoloId
                        ? carenzeMagazzino.find(c => c.articoloId === riga.articoloId)
                        : undefined;
                      const disponibileRiga = riga.articoloId && tipoMovimento === 'entrata' && !isGiroconto
                        ? getDisponibilitaArticolo(riga.articoloId)
                        : null;
                      return (
                        <div key={riga.id} style={{
                          padding: '8px',
                          backgroundColor: 'var(--background-paper)',
                          borderRadius: '4px',
                          border: carenzaRiga ? '1px solid var(--error-main, #d32f2f)' : '1px solid var(--divider)'
                        }}>
                        <div className={styles.rigaGrid}>
                          {riga.tipoRiga === 'voce' ? (
                            <div className={styles.voceDescrizione}>
                              <input
                                type="text"
                                className={styles.formControl}
                                placeholder="Voce o cerca articolo (es. Sconto merce)"
                                value={riga.voce || ''}
                                onChange={(e) => handleUpdateRiga(riga.id, 'voce', e.target.value)}
                                onFocus={() => setRicercaVoce({ rigaId: riga.id, testoIniziale: riga.voce || '' })}
                                onBlur={() => setRicercaVoce(null)}
                                maxLength={255}
                                disabled={isViewMode}
                                aria-label="Descrizione voce libera"
                                aria-autocomplete="list"
                              />
                              {ricercaVoce?.rigaId === riga.id && articoliPerVoce(riga.voce || '').length > 0 && (
                                <div className={styles.autocompleteDropdown} role="listbox" aria-label="Articoli da collegare alla voce">
                                  <div className={styles.autocompleteHint}>Collega un articolo da scaricare dal magazzino</div>
                                  {articoliPerVoce(riga.voce || '').map(articolo => (
                                    <div
                                      key={articolo.id}
                                      role="option"
                                      aria-selected={riga.articoloId === articolo.id}
                                      className={styles.autocompleteItem}
                                      // mousedown: arriva prima del blur che chiude l'elenco
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        collegaArticoloAVoce(riga.id, articolo, ricercaVoce.testoIniziale);
                                        setRicercaVoce(null);
                                      }}
                                    >
                                      <span>{articolo.nome}</span>
                                      <span className={styles.autocompleteSubtext}>
                                        {articolo.codice ? `[${articolo.codice}] · ` : ''}
                                        {(articolo.quantita ?? 0) > 0 ? `Giac. ${articolo.quantita}` : 'ESAURITO'}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ) : (
                          <SearchableSelect
                            options={articoliOptions}
                            value={riga.articoloId || null}
                            onChange={(val) => handleUpdateRiga(riga.id, 'articoloId', val ? Number(val) : undefined)}
                            placeholder="Articolo..."
                            clearable={true}
                            disabled={isViewMode}
                          />
                          )}
                          {riga.tipoRiga === 'voce' ? (
                            <span
                              className={styles.voceLiberaIcon}
                              title={riga.articoloId ? "Voce libera: scarica l'articolo collegato" : 'Voce libera: senza articolo di magazzino'}
                            >
                              <Tag size={14} />
                            </span>
                          ) : (
                          <button
                            type="button"
                            onClick={() => { setArticoloModalRigaId(riga.id); setShowArticoloModal(true); }}
                            title="Crea nuovo articolo"
                            style={{
                              padding: '4px',
                              backgroundColor: 'var(--primary-main)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              height: '32px',
                              width: '32px',
                            }}
                          >
                            <PlusCircle size={14} />
                          </button>
                          )}
                          <input
                            type="number"
                            className={styles.formControl}
                            placeholder="Qtà"
                            step="1"
                            min="1"
                            value={riga.quantita}
                            onChange={(e) => handleUpdateRiga(riga.id, 'quantita', e.target.value)}
                            style={{ textAlign: 'center' }}
                          />
                          <input
                            type="number"
                            className={styles.formControl}
                            placeholder="Prezzo"
                            step="0.01"
                            value={riga.prezzoUnitario}
                            onChange={(e) => handleUpdateRiga(riga.id, 'prezzoUnitario', e.target.value)}
                            style={{ textAlign: 'right' }}
                            title="Prezzo unitario (modifica per aggiornare il totale)"
                          />
                          <input
                            type="number"
                            className={styles.formControl}
                            placeholder="Totale"
                            step="0.01"
                            value={editingTotale?.rigaId === riga.id ? editingTotale.value : totaleRiga.toFixed(2)}
                            onFocus={() => setEditingTotale({ rigaId: riga.id, value: totaleRiga.toFixed(2) })}
                            onChange={(e) => {
                              setEditingTotale({ rigaId: riga.id, value: e.target.value });
                              handleUpdateRigaTotale(riga.id, e.target.value);
                            }}
                            onBlur={() => setEditingTotale(null)}
                            style={{
                              textAlign: 'right',
                              fontWeight: 500,
                              backgroundColor: 'var(--background-default)',
                            }}
                            title="Totale riga (modifica per ricalcolare il prezzo unitario)"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveRiga(riga.id)}
                            style={{
                              padding: '6px',
                              backgroundColor: 'transparent',
                              color: 'var(--error-main)',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                            title="Rimuovi riga"
                          >
                            <X size={16} />
                        </button>
                      </div>
                      {riga.tipoRiga === 'voce' && (
                        <div className={styles.voceLiberaDettagli}>
                          <div className={styles.voceLiberaReparto}>
                            <SearchableSelect
                              options={categorieOptions}
                              value={riga.categoriaId || null}
                              onChange={(val) => handleUpdateRiga(riga.id, 'categoriaId', val ? Number(val) : undefined)}
                              placeholder="Reparto: come l'ordine"
                              clearable={true}
                              disabled={isViewMode}
                            />
                          </div>
                          {riga.articoloId && (
                            <span className={styles.voceArticolo}>
                              <Package size={13} />
                              {riga.articoloNome || articoli.find(a => a.id === riga.articoloId)?.nome || 'Articolo'}
                              {!isViewMode && (
                                <button
                                  type="button"
                                  onClick={() => collegaArticoloAVoce(riga.id, undefined)}
                                  title="Scollega l'articolo"
                                  aria-label="Scollega l'articolo dalla voce"
                                >
                                  <X size={12} />
                                </button>
                              )}
                            </span>
                          )}
                        </div>
                      )}
                      {disponibileRiga !== null && (
                        <div style={{
                          marginTop: '6px',
                          fontSize: '0.75rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          color: carenzaRiga ? 'var(--error-main, #d32f2f)' : 'var(--text-secondary)',
                          fontWeight: carenzaRiga ? 600 : 400,
                        }}>
                          {carenzaRiga && <AlertTriangle size={13} />}
                          {carenzaRiga
                            ? (carenzaRiga.disponibile <= 0
                                ? 'Articolo esaurito: giacenza 0, va rifornito prima di venderlo'
                                : `Giacenza insufficiente: disponibili ${carenzaRiga.disponibile}, richiesti ${carenzaRiga.richiesto}`)
                            : `Giacenza disponibile: ${disponibileRiga}`}
                        </div>
                      )}
                      </div>
                      );
                    })}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: '8px',
                      padding: '8px',
                      backgroundColor: 'var(--primary-light)',
                      borderRadius: '4px'
                    }}>
                      <div className={styles.aggiungiRigaActions}>
                        <button
                          type="button"
                          onClick={() => handleAddRiga()}
                          disabled={isViewMode}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: 'var(--primary-main)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '13px',
                          }}
                        >
                          <PlusCircle size={14} />
                          Articolo
                        </button>
                        <button
                          type="button"
                          onClick={() => handleAddVoce()}
                          disabled={isViewMode}
                          className={styles.btnVoceLibera}
                          title="Riga senza articolo: spedizione, imballo, contributo, sconto..."
                        >
                          <Tag size={14} />
                          Voce libera
                        </button>
                      </div>
                      <div style={{ fontWeight: 600, fontSize: '14px' }}>
                        Totale Righe: {formatCurrency(calcolaTotaleRighe())}
                      </div>
                    </div>
                    {!isViewMode && vociRicorrenti.length > 0 && (
                      <div className={styles.vociRicorrenti}>
                        <span className={styles.vociRicorrentiLabel}>Voci frequenti:</span>
                        {vociRicorrenti.slice(0, 8).map((v) => (
                          <button
                            key={v.voce}
                            type="button"
                            className={styles.voceChip}
                            onClick={() => handleAddVoce(v)}
                            title={`Aggiungi "${v.voce}"${v.categoriaNome ? ` (reparto ${v.categoriaNome})` : ''}`}
                          >
                            <PlusCircle size={12} />
                            {v.voce} · {formatCurrency(v.prezzoUnitario)}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Pulsante per attivare multi-riga */}
              {righeMovimento.length === 0 && !isGiroconto && (
                <div style={{
                  padding: '12px',
                  backgroundColor: 'var(--background-default)',
                  borderRadius: '4px',
                  marginBottom: '16px',
                  textAlign: 'center'
                }}>
                  <button
                    type="button"
                    onClick={() => {
                      // Reset campi articolo singolo e attiva modalità multi-riga
                      setFormArticoloId(undefined);
                      setFormQuantita('1');
                      setFormImporto('');
                      handleAddRiga();
                    }}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: 'transparent',
                      color: 'var(--primary-main)',
                      border: '1px dashed var(--primary-main)',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '13px',
                    }}
                  >
                    <List size={16} />
                    Inserisci più righe: articoli e voci libere (fattura/DDT)
                  </button>
                </div>
              )}

              {/* Sezione: Pagamento */}
              <div className={styles.section}>
                <h4 className={styles.sectionHeader}>
                  <Wallet size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                  Pagamento
                </h4>
                <div className={`${styles.grid} ${styles.grid2}`}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Importo *</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      pattern="[0-9]*[.,]?[0-9]*"
                      className={`${styles.formControl} ${formErrors.importo ? styles.error : ''}`}
                      placeholder="0.00"
                      value={righeMovimento.length > 0 ? calcolaTotaleRighe().toFixed(2) : formImporto}
                      onChange={(e) => {
                        // Accetta solo numeri, punto e virgola (per decimali)
                        const value = e.target.value.replace(',', '.');
                        if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
                          setFormImporto(value);
                          setFormErrors(prev => ({...prev, importo: false}));
                        }
                      }}
                      readOnly={righeMovimento.length > 0}
                      style={righeMovimento.length > 0 ? { backgroundColor: 'var(--background-default)' } : {}}
                    />
                    {formErrors.importo && <span className={styles.errorText}>Campo obbligatorio</span>}
                    {righeMovimento.length > 0 && <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Calcolato dalle righe</span>}
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>{isGiroconto ? 'Da Risorsa *' : 'Risorsa *'}</label>
                    <select
                      className={`${styles.formControl} ${formErrors.risorsaId ? styles.error : ''}`}
                      value={formRisorsaId || ''}
                      onChange={(e) => { setFormRisorsaId(e.target.value ? parseInt(e.target.value) : undefined); setFormErrors(prev => ({...prev, risorsaId: false})); }}
                    >
                      <option value="">Seleziona risorsa...</option>
                      {risorse.filter(r => !isGiroconto || r.id !== formRisorsaDestinazioneId).map((risorsa) => (
                        <option key={risorsa.id} value={risorsa.id}>
                          {risorsa.nome}
                        </option>
                      ))}
                    </select>
                    {formErrors.risorsaId && <span className={styles.errorText}>Campo obbligatorio</span>}
                  </div>
                  {isGiroconto && (
                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>A Risorsa *</label>
                      <select
                        className={`${styles.formControl} ${(formErrors as {risorsaDestinazioneId?: boolean}).risorsaDestinazioneId ? styles.error : ''}`}
                        value={formRisorsaDestinazioneId || ''}
                        onChange={(e) => { setFormRisorsaDestinazioneId(e.target.value ? parseInt(e.target.value) : undefined); setFormErrors(prev => ({...prev, risorsaDestinazioneId: false})); }}
                      >
                        <option value="">Seleziona risorsa destinazione...</option>
                        {risorse.filter(r => r.id !== formRisorsaId).map((risorsa) => (
                          <option key={risorsa.id} value={risorsa.id}>
                            {risorsa.nome}
                          </option>
                        ))}
                      </select>
                      {(formErrors as {risorsaDestinazioneId?: boolean}).risorsaDestinazioneId && <span className={styles.errorText}>Campo obbligatorio</span>}
                    </div>
                  )}
                  {isGiroconto && (
                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>N. Ordine</label>
                      <input
                        type="text"
                        className={styles.formControl}
                        placeholder="Numero ordine (facoltativo)..."
                        value={formOrdineGiroconto}
                        onChange={(e) => setFormOrdineGiroconto(e.target.value)}
                      />
                    </div>
                  )}
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Data *</label>
                    <input
                      type="date"
                      className={`${styles.formControl} ${formErrors.data ? styles.error : ''}`}
                      value={formData}
                      onChange={(e) => { setFormData(e.target.value); setFormErrors(prev => ({...prev, data: false})); }}
                    />
                    {formErrors.data && <span className={styles.errorText}>Campo obbligatorio</span>}
                  </div>
                  <div className={styles.formGroup} style={{ display: 'flex', alignItems: 'center', paddingTop: '28px' }}>
                    {isGiroconto ? (
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                        <CheckCircle size={14} style={{ verticalAlign: 'middle', marginRight: '4px', color: 'var(--success-main)' }} />
                        I giroconti sono sempre pagati
                      </span>
                    ) : rateEnabled ? (
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                        <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                        Le rate vengono create come non pagate
                      </span>
                    ) : (
                      <label className={styles.checkboxWrapper}>
                        <input
                          type="checkbox"
                          className={styles.checkbox}
                          checked={formPagato}
                          onChange={(e) => setFormPagato(e.target.checked)}
                        />
                        <CheckCircle size={16} style={{ color: formPagato ? 'var(--success-main)' : 'var(--text-hint)' }} />
                        <span>{formPagato ? 'Pagato' : 'Non Pagato'}</span>
                      </label>
                    )}
                  </div>
                </div>
              </div>

              {/* Sezione: Rate Automatiche (only for new movements, not giroconti) */}
              {!editingMovimento && !isGiroconto && (
                <div className={styles.section}>
                  <div className={styles.sectionHeader}>
                    <Calendar size={16} style={{ marginRight: '6px' }} />
                    Rate Automatiche
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.checkboxWrapper}>
                      <input
                        type="checkbox"
                        className={styles.checkbox}
                        checked={rateEnabled}
                        onChange={(e) => setRateEnabled(e.target.checked)}
                      />
                      <span>Crea rate automatiche</span>
                    </label>
                  </div>
                  {rateEnabled && (
                    <div className={styles.formRow}>
                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Numero Rate</label>
                        <input
                          type="number"
                          className={styles.formControl}
                          min="2"
                          max="120"
                          value={formNumeroRate}
                          onChange={(e) => setFormNumeroRate(e.target.value)}
                        />
                      </div>
                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Frequenza</label>
                        <select
                          className={styles.formControl}
                          value={formFrequenzaRate}
                          onChange={(e) => setFormFrequenzaRate(e.target.value)}
                        >
                          <option value="MENSILE">Mensile</option>
                          <option value="BIMESTRALE">Bimestrale</option>
                          <option value="TRIMESTRALE">Trimestrale</option>
                          <option value="SEMESTRALE">Semestrale</option>
                          <option value="ANNUALE">Annuale</option>
                        </select>
                      </div>
                      <div className={styles.formGroup} style={{ display: 'flex', alignItems: 'center', paddingTop: '28px' }}>
                        <label className={styles.checkboxWrapper}>
                          <input
                            type="checkbox"
                            className={styles.checkbox}
                            checked={formDividiImporto}
                            onChange={(e) => setFormDividiImporto(e.target.checked)}
                          />
                          <span>Dividi importo totale</span>
                        </label>
                      </div>
                    </div>
                  )}
                  {rateEnabled && (
                    <div className={styles.ratePreview}>
                      <small style={{ color: 'var(--text-secondary)' }}>
                        {formDividiImporto
                          ? `Verranno create ${formNumeroRate} rate da ${formatCurrency(parseFloat(formImporto || '0') / parseInt(formNumeroRate || '1'))} ciascuna`
                          : `Verranno create ${formNumeroRate} rate da ${formatCurrency(parseFloat(formImporto || '0'))} ciascuna`}
                      </small>
                    </div>
                  )}
                </div>
              )}

              {/* Sezione: Note */}
              <div className={styles.section}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Note / Descrizione</label>
                  <textarea
                    className={styles.formControl}
                    placeholder="Aggiungi note..."
                    rows={2}
                    value={formNote}
                    onChange={(e) => setFormNote(e.target.value)}
                    style={{ resize: 'vertical' }}
                  />
                </div>
              </div>

              {/* Allegati Section - only shown when editing */}
              {editingMovimento && (
                <div className={styles.section}>
                  <div className={styles.sectionHeader}>
                    <Paperclip size={16} style={{ marginRight: '6px' }} />
                    Allegati ({allegati.length})
                  </div>

                  {/* Upload button */}
                  <div className={styles.formGroup}>
                    <label className={styles.uploadBtn}>
                      <input
                        type="file"
                        style={{ display: 'none' }}
                        onChange={handleUploadAllegato}
                        disabled={uploadingAllegato}
                      />
                      <Upload size={16} />
                      {uploadingAllegato ? 'Caricamento...' : 'Carica file'}
                    </label>
                  </div>

                  {/* Allegati list */}
                  {allegati.length > 0 && (
                    <div className={styles.allegatiList}>
                      {allegati.map(allegato => (
                        <div key={allegato.id} className={styles.allegatoItem}>
                          <div className={styles.allegatoInfo}>
                            <File size={16} />
                            <span className={styles.allegatoNome}>{allegato.nomeFile}</span>
                            <span className={styles.allegatoSize}>({formatFileSize(allegato.dimensione)})</span>
                          </div>
                          <div className={styles.allegatoActions}>
                            <button
                              className={styles.btnIcon}
                              onClick={() => handleDownloadAllegato(allegato)}
                              title="Scarica"
                            >
                              <Download size={14} />
                            </button>
                            <button
                              className={styles.btnIcon}
                              onClick={() => handleDeleteAllegato(allegato.id)}
                              title="Elimina"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className={`${styles.bodyXsmall} ${styles.mt4}`} style={{ color: 'var(--text-secondary)' }}>
                * Campi obbligatori
              </div>
              </fieldset>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.btnSecondary} onClick={() => { setShowModal(false); setIsViewMode(false); }}>
                <X size={16} />
                {isViewMode ? 'Chiudi' : 'Annulla'}
              </button>
              {editingMovimento && (
                <button
                  className={styles.btnSecondary}
                  onClick={() => {
                    setShowModal(false);
                    handleDuplicaMovimento(editingMovimento);
                  }}
                >
                  <Copy size={16} />
                  Duplica
                </button>
              )}
              {!isViewMode && (
                <button className={styles.btnPrimary} onClick={handleSaveMovimento}>
                  <Save size={16} />
                  Salva
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quick Article Creation Modal */}
      {showArticoloModal && (
        <div className={styles.modalOverlay} onMouseDown={(e) => { if (e.target === e.currentTarget) setShowArticoloModal(false); }}>
          <div className={styles.modalConfirm} style={{ maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.confirmTitle}>Nuovo Articolo</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px' }}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Nome *</label>
                <input
                  type="text"
                  className={styles.formControl}
                  placeholder="Nome articolo..."
                  value={nuovoArticoloNome}
                  onChange={(e) => setNuovoArticoloNome(e.target.value)}
                  autoFocus
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Codice</label>
                <input
                  type="text"
                  className={styles.formControl}
                  placeholder="Codice articolo..."
                  value={nuovoArticoloCodice}
                  onChange={(e) => setNuovoArticoloCodice(e.target.value)}
                />
              </div>
              <div className={styles.quickGrid2}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Prezzo Acquisto</label>
                  <input
                    type="number"
                    className={styles.formControl}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    value={nuovoArticoloPrezzoAcquisto}
                    onChange={(e) => setNuovoArticoloPrezzoAcquisto(e.target.value)}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Prezzo Vendita</label>
                  <input
                    type="number"
                    className={styles.formControl}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    value={nuovoArticoloPrezzoVendita}
                    onChange={(e) => setNuovoArticoloPrezzoVendita(e.target.value)}
                  />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Categoria Merceologica *</label>
                <select
                  className={styles.formControl}
                  value={nuovoArticoloCategoriaArticoloId || ''}
                  onChange={(e) => setNuovoArticoloCategoriaArticoloId(e.target.value ? Number(e.target.value) : undefined)}
                  disabled={categorieArticoli.length === 0}
                >
                  <option value="">-- Seleziona categoria --</option>
                  {categorieArticoli.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.nome}</option>
                  ))}
                </select>
                {categorieArticoli.length === 0 && (
                  <span style={{ color: 'var(--warning-main)', fontSize: '0.8rem' }}>
                    Nessuna categoria disponibile.{' '}
                    <Link to="/categorie-articoli" style={{ textDecoration: 'underline', fontWeight: 600 }}>Creane una</Link>
                    {' '}prima di aggiungere un articolo.
                  </span>
                )}
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Giacenza Iniziale</label>
                <input
                  type="number"
                  className={styles.formControl}
                  placeholder="0"
                  step="1"
                  min="0"
                  value={nuovoArticoloQuantita}
                  onChange={(e) => setNuovoArticoloQuantita(e.target.value)}
                />
              </div>
            </div>
            <div className={styles.confirmActions} style={{ marginTop: '20px' }}>
              <button className={styles.btnSecondary} onClick={() => setShowArticoloModal(false)} disabled={savingArticolo}>
                Annulla
              </button>
              <button className={styles.btnPrimary} onClick={handleSaveNuovoArticolo} disabled={savingArticolo}>
                {savingArticolo ? 'Salvataggio...' : 'Crea Articolo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Contact Creation Modal */}
      {showContattoModal && (
        <div className={styles.modalOverlay} onMouseDown={(e) => { if (e.target === e.currentTarget) setShowContattoModal(false); }}>
          <div className={styles.modalConfirm} style={{ maxWidth: '450px' }} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.confirmTitle}>Nuovo Contatto</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px' }}>
              <div className={styles.quickGrid2}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Nome / Ragione Sociale *</label>
                  <input
                    type="text"
                    className={styles.formControl}
                    placeholder="Nome o ragione sociale..."
                    value={nuovoContattoNome}
                    onChange={(e) => setNuovoContattoNome(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Codice Cliente</label>
                  <input
                    type="text"
                    className={styles.formControl}
                    placeholder="Auto se vuoto"
                    value={nuovoContattoCodice}
                    onChange={(e) => setNuovoContattoCodice(e.target.value)}
                  />
                </div>
              </div>
              <div className={styles.quickGrid2}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Email</label>
                  <input
                    type="email"
                    className={styles.formControl}
                    placeholder="email@esempio.it"
                    value={nuovoContattoEmail}
                    onChange={(e) => setNuovoContattoEmail(e.target.value)}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Telefono</label>
                  <input
                    type="tel"
                    className={styles.formControl}
                    placeholder="+39..."
                    value={nuovoContattoTelefono}
                    onChange={(e) => setNuovoContattoTelefono(e.target.value)}
                  />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Tipo</label>
                <select
                  className={styles.formControl}
                  value={nuovoContattoTipo}
                  onChange={(e) => setNuovoContattoTipo(e.target.value)}
                >
                  {tipiContatto.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className={styles.confirmActions} style={{ marginTop: '20px' }}>
              <button className={styles.btnSecondary} onClick={() => setShowContattoModal(false)} disabled={savingContatto}>
                Annulla
              </button>
              <button className={styles.btnPrimary} onClick={handleSaveNuovoContatto} disabled={savingContatto}>
                {savingContatto ? 'Salvataggio...' : 'Crea Contatto'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteTarget && (
        <div className={styles.modalOverlay} onMouseDown={(e) => { if (e.target === e.currentTarget) setShowDeleteModal(false); }}>
          <div className={styles.modalConfirm} onClick={(e) => e.stopPropagation()}>
            <div className={styles.confirmIcon}>
              <AlertTriangle size={40} />
            </div>
            <h3 className={styles.confirmTitle}>Eliminare questo movimento?</h3>
            <p className={styles.confirmText}>
              Stai per eliminare il movimento del <strong>{new Date(deleteTarget.data).toLocaleDateString('it-IT')}</strong> di <strong>{formatCurrency(deleteTarget.importo)}</strong>.
              Questa azione non può essere annullata.
            </p>
            <div className={styles.confirmActions}>
              <button className={styles.btnSecondary} onClick={() => setShowDeleteModal(false)}>
                Annulla
              </button>
              <button className={styles.btnDanger} onClick={handleConfirmDelete}>
                <Trash2 size={16} />
                Elimina
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stampa ordine per la spedizione: scelta del formato */}
      {ordineDaStampare && (
        <div className={styles.modalOverlay} onMouseDown={(e) => { if (e.target === e.currentTarget) setOrdineDaStampare(null); }}>
          <div className={styles.modalConfirm} onClick={(e) => e.stopPropagation()}>
            <div className={styles.confirmIcon}>
              <Printer size={40} />
            </div>
            <h3 className={styles.confirmTitle}>Stampa ordine {ordineDaStampare.numeroOrdine}</h3>
            <p className={styles.confirmText}>
              {ordineDaStampare.righe.length} {ordineDaStampare.righe.length === 1 ? 'articolo' : 'articoli'}
              {ordineDaStampare.destinatario ? <> · destinatario <strong>{ordineDaStampare.destinatario.nome}</strong></> : null}.
              {' '}Scegli il documento da allegare alla spedizione.
              {!datiAzienda && (
                <>
                  {' '}<br />
                  <small>
                    Suggerimento: compila i dati azienda in Impostazioni per avere l'intestazione sul documento.
                  </small>
                </>
              )}
            </p>
            <div className={styles.confirmActions} style={{ flexDirection: 'column', gap: 10 }}>
              <button className={styles.btnPrimary} onClick={() => eseguiStampaOrdine(true, 'stampa')}>
                <Printer size={16} />
                Stampa con prezzi e totale
              </button>
              <button className={styles.btnPrimary} onClick={() => eseguiStampaOrdine(false, 'stampa')}>
                <Printer size={16} />
                Stampa senza prezzi (packing list)
              </button>
              <button className={styles.btnSecondary} onClick={() => eseguiStampaOrdine(true, 'download')}>
                <Download size={16} />
                Scarica PDF con prezzi
              </button>
              <button className={styles.btnSecondary} onClick={() => eseguiStampaOrdine(false, 'download')}>
                <Download size={16} />
                Scarica PDF senza prezzi
              </button>
              <button className={styles.btnSecondary} onClick={() => setOrdineDaStampare(null)}>
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
